// 1. React imports
import React, { KeyboardEvent, useEffect, useState } from 'react';

// Re-export utility functions from separate files for backward compatibility
export { insertAt } from './arrayUtils';
export { checkIfDateHasPassed, checkIfEmailIsValid, checkIsValidJsonString } from './validationUtils';

// Import for internal use
import { checkIfEmailIsValid, checkIfDateHasPassed } from './validationUtils';

// 2. Third-party library imports
import { App, Button, Skeleton, Popover, Row, Col } from 'antd';
import { useSelector } from 'react-redux';
import {
    capitalize,
    clone,
    debounce,
    difference,
    filter,
    find,
    findIndex,
    first,
    forEach,
    get,
    groupBy,
    has,
    includes,
    isEmpty,
    isEqual,
    isUndefined,
    last,
    map,
    omitBy,
    orderBy,
    reduce,
    some,
    startsWith,
    sum,
    times,
    toLower,
    toString,
    upperCase,
    upperFirst
} from 'lodash-es';
import { print } from 'graphql/language/printer';
import { DocumentNode } from 'graphql';
import { Predictions } from '@aws-amplify/predictions';
import { fetchAuthSession } from '@aws-amplify/auth';
import { I18n } from 'aws-amplify/utils';
import QueryString from 'query-string';
import Papa from 'papaparse';
import Cookies from 'js-cookie';
import invert from 'invert-color';
import { Parser } from 'html-to-react';
import { v4 as uuidv4 } from 'uuid';
import dayjs from 'src/config/dayjs';
import type { Dayjs } from 'dayjs';

// 3. Constants
import {
    appliedFilterIndicator,
    customFieldIndicator,
    getMinMaxIndicator,
    treeParentChildSeparator,
} from '../constants/filterbar';
import {
    ASSETS_LINK_DEV,
    ASSETS_LINK_NONPROD,
    ASSETS_LINK_PROD,
    ASSETS_LINK_TEST,
    ASSETS_LINK_UAT,
    COOKIE_LAST_ACTIVE_NAME,
    FETCH_ANNOUNCEMENTS_ON_LOGIN_TOKEN_NAME,
    TIMEOUT_MILLISECOND,
    TIME_ACTIVE_COOKIE_SETTING,
    confirmModalOkText,
    confirmModalCancelText,
    initialSecondaryColor,
    xlMin
} from '../config/config';
import { DETAILS_TAB, USERS_PAGE } from '../config/tableAndPageConstants';
import {
    conversationTypeClassifications,
    conversationTypes,
    fileTypesSignature,
    MYOB_SHOW_ATTACHMENT_URL} from '../constants/common';
import { notQueryRelatedKeys } from '../constants/dashboardUtils';
import {
    dateFormatFileDownload,
    dateFormatTimestamp,
    dateFormatYYYYMMDDDash,
    dateFormatYYYYMMDDTHHmmssDash,
    dateFormatYYYYMMDDTHHmmssSSSSSSUTCDash
} from '../constants/dateFormats';
import { dateSelectOptions, invoiceChangeTypes } from '../constants/invoicesSortAndFilters';
import {
    accountingSystemOptions,
    companyApiImportTypes,
    companyCloudImportTypes,
    companyImportTypes,
    hiddenCloudImportFields
} from '../constants/settings';
import { companyStatuses } from '../constants/organisationsSortAndFilters';
import { ContactFilterOptions } from '../constants/tasksSortAndFilters';
import { paymentPlanActions } from '../constants/paymentPlansSortAndFilters';
import { creditChangeTypes } from '../constants/creditsSortAndFilters';
import { IsOrganisationViewAttribute } from '../constants/authUserAttributes';

// 4. Components
import FontAwesome from '../components/common/FontAwesome';

// 5. Utility functions
import I18nWrapper from './I18nWrapper';
import { DynamicObject } from './commonInterfaces';

// 6. Redux and other project-specific modules
import store from 'src/store/store';
import client, { delApi, delRegionalApi, executePostApi, getRegionalApi, postRegionalApi } from 'src/api';

// 7. Types
import { Company, CompanyUserRole, customFieldChangeTypes } from '../store/companies/types';
import type { CompanyBase } from '../store/entity-types';
import { CustomField } from '../store/common/types';
import {
    Contact as ContactType,
} from '../store/customers/types';
import type { CustomerBase as CustomerType } from '../store/entity-types';
import { DeliveryDetails } from '../store/invoices/types';
import { Organisation, OrganisationActionTab } from '../store/organisations/types';
import { ReportTypeFilter } from '../store/report/types';
import { CreditOrganisationCurrency } from '../store/credits/types';
import { User } from '../store/users/types';

// 8. Redux sagas
import { getCurrentUser } from '../store/auth/authSlice';

// 9. Amplify config
import {
    Environments,
    AMPLIFY_PUBLIC_CONFIG_DEV,
    AMPLIFY_PUBLIC_CONFIG_NONPROD,
    AMPLIFY_PUBLIC_CONFIG_PROD,
    AMPLIFY_PUBLIC_CONFIG_TEST,
    AMPLIFY_PUBLIC_CONFIG_UAT,
} from '../AmplifyConfig';
import { paymentChangeTypes } from '../constants/paymentsSortAndFilters';
import { remittanceAdviceChangeTypes, remittanceAdviceSource } from '../constants/remittanceAdvicesSortAndFilter';
import { RootState } from 'src/store/store';
import { GetTicketDiscussionRequestPayload } from 'src/store/tickets/types';
import { CUSTOMER_PORTAL_API_HEADER_KEY } from 'src/constants/customerPortal';
import { workflowChangeTypes } from '../constants/workflowsSortAndFilters';

/**
 * Function that returns a hex color based on the string given.
 * Used for populating the Avatar icon color for name.
 * @param name - name of user
 */
export const stringToHex = (name: string | undefined) => {
    if (name === undefined) {
        return '#b3b3b3';
    }

    let hash = 0;

    if (name.length === 0) {
        return hash;
    }

    for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
        hash = hash & hash;
    }

    let color = '#';

    for (let x = 0; x < 3; x++) {
        const value = (hash >> (x * 8)) & 255;
        color += ('00' + value.toString(16)).substr(-2);
    }

    return color.toString();
};

/**
 * Function that returns the initial of a given name.
 * @param name - name of the user
 */
export const getNameInitials = (name: string) => {
    const parts = name.split(' ');
    let initials = '';
    const first = parts[0];
    const last = parts[parts.length - 1];
    initials += first.charAt(0).toUpperCase();
    initials += last.charAt(0).toUpperCase();

    return initials;
};

/**
 * Function that adds number of days to date.
 * @param date - date to add the number of days to
 * @param days - number of days to be added to date
 */
export const addDaysToDate = (date: Date, days: number) => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);

    return result;
};

/**
 * Function that computes the table scroll x and y based on required value by Ant Design table.
 * @param tableHeightByWindow - table height estimate
 * @param pageSize - number of items in page
 * @param rowHeight - height of each table row
 * @param tableWidthByWindow - table width estimate
 */
export const computeTableScroll = (
    tableHeightByWindow: number,
    pageSize: number,
    rowHeight: number = USERS_PAGE.rowHeight,
    tableWidthByWindow?: number
) => {
    const idealHeight = rowHeight * pageSize;
    let yValue;

    if (tableHeightByWindow <= idealHeight) {
        yValue = tableHeightByWindow;
    } else {
        yValue = pageSize * (rowHeight - 2);
    }

    const returnValue: any = {
        y: yValue,
    };

    if (tableWidthByWindow) {
        returnValue.x = tableWidthByWindow;
    }

    return returnValue;
};

/**
 * Function that refreshes the cognito session.
 * @param callback - function called after refreshing the session irregardless if there's
 * an error or not
 */
export const refreshCognitoAttributes = async (
    callback?: (err: any, session: any) => void
) => {
    // Fetch the current session; this will automatically refresh tokens if needed
    const session = await fetchAuthSession({ forceRefresh: true });
    const idToken = get(session, 'tokens.idToken', {});
    const userClaims = get(idToken, 'payload', {});

    if (callback) {
        callback(null, userClaims);
    }
};

/**
 * Function that updates the less variables in color.less file used for styling the UI
 * on the go.
 * @param colorVariables - color variables, must conform to the less variables defined
 * @param callback - callback function after variables have been updated
 */
export const updateLessVariables = (
    colorVariables: any,
    callback?: () => void
) => {
    const receivedColorVariables: any = { ...colorVariables };

    const primaryColor = receivedColorVariables['@custom-primary-color'];

    if (primaryColor) {
        receivedColorVariables['@contrast-custom-primary'] = getContrastColor(primaryColor);
    }

    receivedColorVariables['@custom-secondary-color'] = initialSecondaryColor; // overwrite secondary passed with the original grey one that we have
    const secondaryColor = receivedColorVariables['@custom-secondary-color'];

    if (secondaryColor) {
        receivedColorVariables['@contrast-custom-secondary'] = getContrastColor(secondaryColor);
    }

    const documentStyle = document.documentElement.style;

    if (documentStyle) {
        Object.entries(receivedColorVariables).forEach(([key, value]) => {
            if (value) {
                documentStyle.setProperty(key.replace('@', '--'), value as string);
            }
        });
    }
    
    if (callback) {
        callback();
    }
};

/**
 * Function that fetches the inverse of the color. Usually used for getting the font color
 * of a section with background.
 * @param color - color to get the inverse of
 */
export const getContrastColor = (color: any) => {
    const blackWhiteParams = {
        black: '#000000',
        white: '#ffffff',
        threshold: 0.4,
    };

    if (color) {
        return invert(color, blackWhiteParams);
    } else {
        return blackWhiteParams.black;
    }
};

/**
 * Function that gets the popover container based on the ref given.
 * If there's no ref given, or the given ref is invalid, should return the html body.
 * @param ref - ref for the component to be used as the wrapper
 */
export const getPopoverContainer = (ref: any) => {
    if (ref?.current instanceof HTMLElement) {
        return ref.current;
    }

    return document.body;
};

/**
 * Function that returns the correct Company name based on a given logic.
 * @param Customer - customer object from API
 */
export const getCompanyName = (Customer: CustomerType) => {
    if (!isEmpty(Customer)) {
        const customerDisplayName = get(Customer, 'DisplayName');

        if (customerDisplayName) {
            return customerDisplayName;
        } else {
            const { CompanyName, Contacts } = Customer;
            const Contact: ContactType | undefined = get(Contacts, 0);
            const FirstName = get(Contact, 'FirstName');
            const LastName = get(Contact, 'LastName');

            if (!isEmpty(CompanyName)) {
                if (!isEmpty(FirstName)) {
                    return `${CompanyName}, ${FirstName}`;
                } else {
                    return CompanyName;
                }
            } else if (!isEmpty(FirstName) && !isEmpty(LastName)) {
                return `${FirstName} ${LastName}`;
            } else if (!isEmpty(FirstName) && isEmpty(LastName)) {
                return FirstName;
            } else if (isEmpty(FirstName) && !isEmpty(LastName)) {
                return LastName;
            } else {
                return '';
            }
        }
    } else {
        return '';
    }
};

/**
 * Function that gets the full address based on a given contact object.
 * @param Contact - contact object from api
 */
export const getContactAddress = (Contact: ContactType) => {
    let fullAddress = '';

    const addressNeededArray = [
        'AddressLine1',
        'AddressLine2',
        'City',
        'State',
        'Postcode',
    ];

    forEach(addressNeededArray, (addressNeeded: string) => {
        const addressOption = get(Contact, addressNeeded);

        if (!isEmpty(addressOption)) {
            if (!isEmpty(fullAddress)) {
                fullAddress += ', ';
            }

            fullAddress += addressOption;
        }
    });

    return fullAddress;
};

/**
 * Function that return the proper color for each contact method/type.
 * @param Contact - contact object from API
 * @param fullAddress - full address for post address color
 */
export const getContactIconColors = (
    Contact: ContactType,
    fullAddress: string
) => {
    const addressColor = isEmpty(fullAddress) ? 'red' : 'green';
    let mobileColor = '';
    let emailColor = '';

    if (isEmpty(mobileColor)) {
        mobileColor = !isEmpty(Contact.MobileNumber) ? 'green' : 'red';
    }

    if (isEmpty(emailColor)) {
        emailColor = !isEmpty(Contact.Email) ? 'green' : 'red';
    }

    return {
        addressColor,
        mobileColor,
        emailColor,
    };
};

/**
 * Getting the correct indefinete variable (`a` or `an`) based on the succeeding word/phrase.
 * @param text - the succeeding text
 */
export const getIndefiniteArticleBySucceedingWordOrPhrase = (text: string) => {
    // Getting the first word
    const match = /\w+/.exec(text);

    if (match) {
        var word = match[0];
    } else {
        return 'an';
    }

    const l_word = word.toLowerCase();
    // Specific start of words that should be preceeded by 'an'
    const alt_cases = ['honest', 'hour', 'hono'];

    for (const i in alt_cases) {
        if (l_word.indexOf(alt_cases[i]) === 0) {
            return 'an';
        }
    }

    // Single letter word which should be preceeded by 'an'
    if (l_word.length === 1) {
        if ('aedhilmnorsx'.indexOf(l_word) >= 0) {
            return 'an';
        } else {
            return 'a';
        }
    }

    // Capital words which should likely be preceeded by 'an'
    if (
        word.match(
            /(?!FJO|[HLMNS]Y.|RY[EO]|SQU|(F[LR]?|[HL]|MN?|N|RH?|S[CHKLMNPTVW]?|X(YL)?)[AEIOU])[FHLMNRSX][A-Z]/
        )
    ) {
        return 'an';
    }

    // Special cases where a word that begins with a vowel should be preceeded by 'a'
    const regexes = [
        /^e[uw]/,
        /^onc?e\b/,
        /^uni([^nmd]|mo)/,
        /^u[bcfhjkqrst][aeiou]/,
    ];

    for (const r in regexes) {
        if (l_word.match(regexes[r])) {
            return 'a';
        }
    }

    // Special capital words (UK, UN)
    if (word.match(/^U[NK][AIEO]/)) {
        return 'a';
    } else if (word === word.toUpperCase()) {
        if ('aedhilmnorsx'.indexOf(l_word[0]) >= 0) {
            return 'an';
        } else {
            return 'a';
        }
    }

    // Basic method of words that begin with a vowel being preceeded by 'an'
    if ('aeiou'.indexOf(l_word[0]) >= 0) {
        return 'an';
    }

    // Instances where y follwed by specific letters is preceeded by 'an'
    if (l_word.match(/^y(b[lor]|cl[ea]|fere|gg|p[ios]|rou|tt)/)) {
        return 'an';
    }

    return 'a';
};

/**
 * Function that prevents the image from being cached by the browser by appending a timestamp to the path.
 * @param imagePath - current image path that is/will be cached by the browser
 */
export const invalidateImagePathCache = (imagePath: string, time?: number) => {
    const timeUsed = time || new Date().getTime();

    return imagePath + `?removeCachingTime=${timeUsed}`;
};

/**
 * Function that returns the correct assets path based on the build environment.
 */
export const getAssetsPath = () => {
    let assetsPath = ASSETS_LINK_DEV;

    const buildEnvironment = getCurrentBuildEnvironment();

    if (buildEnvironment === Environments.DEV) {
        assetsPath = ASSETS_LINK_DEV;
    } else if (buildEnvironment === Environments.PROD) {
        assetsPath = ASSETS_LINK_PROD;
    } else if (buildEnvironment === Environments.TEST) {
        assetsPath = ASSETS_LINK_TEST;
    } else if (buildEnvironment === Environments.UAT) {
        assetsPath = ASSETS_LINK_UAT;
    } else if (buildEnvironment === Environments.NONPROD) {
        assetsPath = ASSETS_LINK_NONPROD;
    }

    return assetsPath;
};

/**
 * Function that returns the correct amplify config based on the build environment.
 */
export const getPublicAmplifyConfig = () => {
    let publicAmplifyConfig = AMPLIFY_PUBLIC_CONFIG_DEV;
    const buildEnvironment = getCurrentBuildEnvironment();

    if (buildEnvironment === Environments.DEV) {
        publicAmplifyConfig = AMPLIFY_PUBLIC_CONFIG_DEV;
    } else if (buildEnvironment === Environments.PROD) {
        publicAmplifyConfig = AMPLIFY_PUBLIC_CONFIG_PROD;
    } else if (buildEnvironment === Environments.TEST) {
        publicAmplifyConfig = AMPLIFY_PUBLIC_CONFIG_TEST;
    } else if (buildEnvironment === Environments.UAT) {
        publicAmplifyConfig = AMPLIFY_PUBLIC_CONFIG_UAT;
    } else if (buildEnvironment === Environments.NONPROD) {
        publicAmplifyConfig = AMPLIFY_PUBLIC_CONFIG_NONPROD;
    }

    return publicAmplifyConfig;
};

/**
 * Function that check's if the view/device is for mobile/ipad view.
 */
export const getIsMobile = () => {
    return window.innerWidth < 758; //For size md
};

/**
 * Function that gets the current build environment.
 * Uses import.meta.env.MODE to determine environment without circular dependency.
 */
export const getCurrentBuildEnvironment = (): Environments => {
    const mode = import.meta.env.MODE;
    
    if (mode === 'development' || mode === 'dev') {
        return Environments.DEV;
    }
    if (mode === 'test') {
        return Environments.TEST;
    }
    if (mode === 'pretest') {
        return Environments.PRETEST;
    }
    if (mode === 'uat') {
        return Environments.UAT;
    }
    if (mode === 'nonprod') {
        return Environments.NONPROD;
    }
    if (mode === 'production' || mode === 'prod') {
        return Environments.PROD;
    }
    
    return Environments.DEV;
};

/**
 * Function that removes the indicator filters used for populating the filter bar section which the API doesn't really need.
 * @param filters - filters to be passed API as payload
 * @param processFilters - indicator if filter payload is to be processed or not
 * @param taskFilterProcessor - manages processor to be used for manipulating the filter payload is the one used in tasks page
 */
export const removeAppliedFiltersForApiRequest = (
    filters: {},
    processFilters: boolean = false,
    taskFilterProcessor: string = 'customer',
    useLocalTime: boolean = true,
    customerLabel: string = "Customer",
    isCustomerParentView: boolean = false
) => {
    const cleanFilterList: any = {};
    const isCalendarView = get(filters, 'isCalendarView', false);
    let filtersUsed = filters;
    const customFieldsFilters: DynamicObject[] = [];
    const CustomFieldMultipleValuesFilters: DynamicObject[] = [];

    if (processFilters) {
        if ( taskFilterProcessor === 'customer' ) {
            filtersUsed =
                populateFilterCalculationsForPayloadCustomer(filters, isCustomerParentView);
        } else if (
            taskFilterProcessor === 'invoice' ||
            taskFilterProcessor === 'credit'
        ) {
            filtersUsed = populateFilterCalculationsForPayloadInvoices(filters);
        } else if (taskFilterProcessor === 'task') {
            filtersUsed = populateFilterCalculationsForPayloadTask(filters);
        } else if (taskFilterProcessor === 'ticket') {
            filtersUsed = populateFilterCalculationsForPayloadTicket(filters);
        } else if (taskFilterProcessor === 'organisation') {
            filtersUsed =
                populateFilterCalculationsForPayloadOrganisation(filters);
        } else if (taskFilterProcessor === 'paymentPlans') {
            filtersUsed =
                populateFilterCalculationsForPayloadPaymentPlans(filters);
        } else if (taskFilterProcessor === 'payments') {
            filtersUsed = populateFilterCalculationsForPayloadPayments(filters);
        } else if (taskFilterProcessor === 'changelines') {
            filtersUsed = populateFilterCalculationsForPayloadChangeLines(filters);
        } else if (taskFilterProcessor === 'remittanceAdvices') {
            filtersUsed = populateFilterCalculationsForPayloadRemittanceAdvices(filters);
        } else if (taskFilterProcessor === 'report') {
            filtersUsed = populateFilterCalculationsForPayloadReports(filters, customerLabel);
        }
    }

    forEach (filtersUsed, (filter: any, keyName: string) => {
        if (includes(keyName, customFieldIndicator)) {
            const baseKey = keyName.replace(appliedFilterIndicator, '');
            const baseFilter = get(filtersUsed, baseKey);
            const dateValueChecker = baseFilter && baseFilter.value &&
            Object.values(dateSelectOptions).includes(baseFilter.value);
  
            const usedKeyValue = keyName
                    .replace(customFieldIndicator, '')
                    .replace(appliedFilterIndicator, '');
            const customFieldKeyValArray = usedKeyValue.split('--');

            if (includes(keyName, appliedFilterIndicator) && filter && (!baseFilter || !baseFilter.AmountType ) && !dateValueChecker) {
                if (Array.isArray(filter)) {
                    if (!isEmpty(filter)) {
                        CustomFieldMultipleValuesFilters.push({
                            Type: get(customFieldKeyValArray, 0),
                            Name: get(customFieldKeyValArray, 1),
                            Values: filter,
                        });
                    }                        
                } else {
                    customFieldsFilters.push({
                        Type: get(customFieldKeyValArray, 0),
                        Name: get(customFieldKeyValArray, 1),
                        Value: filter,
                    });
                }
            } else if (!includes(keyName, appliedFilterIndicator) &&
                filter && (filter.AmountType || filter.AmountOperator || filter.AmountValue)
            ){
                const numberFilterValue = {
                    ...filter,
                    NumberType: filter.AmountType,
                    NumberValue: filter.AmountValue,
                    NumberOperator: filter.AmountOperator,
                };

                delete numberFilterValue.AmountType;
                delete numberFilterValue.AmountValue;
                delete numberFilterValue.AmountOperator;

                customFieldsFilters.push({
                    Type: get(customFieldKeyValArray, 0),
                    Name: get(customFieldKeyValArray, 1),
                    NumberFilter: numberFilterValue,
                });
            } else if ((!includes(keyName, appliedFilterIndicator) &&
                filter && dateValueChecker)
            ) {
                const { minDate, maxDate } = getDateFilterValues(
                    get(filters, keyName),
                    useLocalTime
                );

                customFieldsFilters.push({
                    Type: get(customFieldKeyValArray, 0),
                    Name: get(customFieldKeyValArray, 1),
                    DateFilter: {
                        DateMin: minDate,
                        DateMax: maxDate
                    },
                });
            }

            delete cleanFilterList[keyName];
        } else if (includes(keyName, getMinMaxIndicator)) {
            const dateFilterName = keyName.replace(getMinMaxIndicator, '');
            const { minDate, maxDate } = getDateFilterValues(
                get(filters, dateFilterName),
                useLocalTime,
                isCalendarView
            );
            cleanFilterList[`${dateFilterName}Min`] = minDate;
            cleanFilterList[`${dateFilterName}Max`] = maxDate;
        } else if (
            !includes(keyName, appliedFilterIndicator) &&
            (!(includes(keyName, 'Date') && get(filter, 'value')) ||
             !(includes(keyName, 'OnHoldUntil') && get(filter, 'value')) ||
             !(includes(keyName, 'VipUntil') && get(filter, 'value'))
            ) // For date objects
        ) {
            cleanFilterList[keyName] = filter;
        }
    });

    if (!isEmpty(CustomFieldMultipleValuesFilters)) {
        cleanFilterList.CustomFieldMultipleValuesFilters =
            JSON.stringify(CustomFieldMultipleValuesFilters);
    }

    if (!isEmpty(customFieldsFilters)) {
        cleanFilterList.CustomFieldFilters =
            JSON.stringify(customFieldsFilters);
    }

    return cleanFilterList;
};

export const getTicketDiscussionPayload = (payload: any) => {
    const { record, currentPage, companyId } = payload as GetTicketDiscussionRequestPayload;
    
    const pageSize = DETAILS_TAB.CONVERSATION_TIMELINE.pageSize;
    const allTicketDiscussionFilter = {
        Type: [16],
        [`Type${appliedFilterIndicator}`]: [16],
    };
    const filters = {
        ResourceScopes: [{
            Resource: {
                Uri: record.Uri,
            },
            Scope: 'All',
        }], ...allTicketDiscussionFilter
    };

    const cleanFilters = removeAppliedFiltersForApiRequest(
        filters,
        true,
        undefined,
        true
    );

    const finalPayload = {
        ...cleanFilters,
        SortField: 'Created Date Time',
        Ascending: false,
        PageSize: pageSize,
        Skip: currentPage * pageSize,
        CompanyId: companyId
    };

    return {
        finalPayload,
        pageSize,
        currentPage
    };
};

/*
* Function that returns the numerical value for contact type
* @param deliveryType - deliverystatus Type
* @param smsDeliveryType - smsDeliveryStatus Type
*/
export const getContactTypeValue = (deliveryDetails: DeliveryDetails[]) => {
    let contactTypes = 0;

    forEach (deliveryDetails, (value: DeliveryDetails) => {
        if (get(value, 'Type') === "Email") {
            contactTypes = contactTypes + ContactFilterOptions['Email'];
        } else if (get(value, 'Type') === "SMS") {
            contactTypes = contactTypes + ContactFilterOptions['Mobile'];
        }
    });

    return contactTypes;
};

export const getTimelineIconAndColor = ({
    type,
    typeClassification,
    isRecentComment,
    context,
    item,
    extraProps,
    modal
}: {
    type: string;
    typeClassification: string;
    isRecentComment?: boolean;
    context?: any;
    item?: any;
    extraProps?: DynamicObject;
    modal?: ReturnType<typeof App.useApp>['modal'];
}): {
    icon: React.ReactNode;
    color?: string;
    label?: React.ReactNode;
} => {
    const defaultResult = {
        icon: null,
        color: undefined
    };

    // ------------------------------
    // CREDIT ITEM TYPE
    // ------------------------------
    if (extraProps?.itemType === 'credit') {
        // Credit change (special case)
        if (type === creditChangeTypes.Allocation ||
            type === creditChangeTypes.Deallocation ||
            type === creditChangeTypes.NewResource ||
            type === creditChangeTypes.UpdatedResource) {

            const shouldShowDeallocate = 
                extraProps?.allowedCreditsDeallocation &&
                type === creditChangeTypes.Allocation &&
                get(item, 'IsReversed') === false;

            const deallocateCreditHandler = () => {
                if (!modal) return;

                modal.confirm({
                    title: getTranslatedText("Deallocate credit"),
                    content: <div>{getTranslatedText("Are you sure you want to deallocate this credit?")}</div>,
                    onOk: () => {
                        if (extraProps?.deallocateCredit) {
                            extraProps?.deallocateCredit(item);
                        }
                    },
                    okText: getTranslatedText(confirmModalOkText),
                    cancelText: getTranslatedText(confirmModalCancelText),
                    getContainer: () => getPopoverContainer(extraProps?.containerRef),
                });
            };

            let iconBase: React.ReactNode;

            switch (type) {
                case creditChangeTypes.Allocation:
                    iconBase = (
                        <div className="fa-layers fa-fw fa fs-16" style={{ marginInlineStart: '-4px' }}>
                            <FontAwesome className="green" icon={['fa', 'file']} />
                            <FontAwesome className="white" icon={['fa', 'plus']} transform="shrink-8 down-2" />
                        </div>
                    );
                    break;
                case creditChangeTypes.Deallocation:
                    iconBase = (
                        <div className="fa-layers fa-fw fa fs-16" style={{ marginInlineStart: '-4px' }}>
                            <FontAwesome className="red" icon={['fa', 'file']} />
                            <FontAwesome className="white" icon={['fa', 'plus']} transform="shrink-8 down-2" />
                        </div>
                    );
                    break;
                case creditChangeTypes.NewResource:
                case creditChangeTypes.UpdatedResource:
                    iconBase = <FontAwesome icon={['fas', 'retweet']} className="green fs-16" />;
                    break;
            }

            return {
                icon: iconBase,
                color: undefined,
                label: shouldShowDeallocate ? (
                    <Button
                        type="link"
                        onClick={deallocateCreditHandler}
                        style={{ padding: '0 12px', whiteSpace: 'nowrap' }}
                    >
                        <FontAwesome icon={['fas', 'minus-square']} className="mr-4" />
                        <span>{getTranslatedText('Deallocate')}</span>
                    </Button>
                ) : undefined
            };
        }
    }
    // ------------------------------
    // CUSTOM FIELD ITEM TYPE
    // ------------------------------
    if (extraProps?.itemType === 'customField') {
        if (type === customFieldChangeTypes.NewResource ||
            type === customFieldChangeTypes.UpdatedResource) {

            let iconBase: React.ReactNode;

            switch (type) {
                case customFieldChangeTypes.NewResource:
                    iconBase = <FontAwesome icon={['fas', 'receipt']} className="green fs-16" />;
                    break;
                case customFieldChangeTypes.UpdatedResource:
                    if(context.includes('Deleted'))
                        iconBase = <FontAwesome icon={['fas', 'receipt']} className="red fs-16" />;
                    else
                        iconBase = <FontAwesome icon={['fas', 'retweet']} className="green fs-16" />;
                    break;
            }

            return {
                icon: iconBase,
                color: undefined
            };
        }
    }

    // ------------------------------
    // CONVERSATION ITEM TYPE
    // ------------------------------
    else if (extraProps?.itemType === 'conversation') {
        switch (type) {
            case conversationTypes.Comment:
                if (typeClassification === conversationTypeClassifications.Comment) {
                    return {
                        icon: ['fas', 'comment'],
                        color: isRecentComment ? 'blue' : 'grey'
                    };
                }
                break;
            case conversationTypes.DeliveryTracking:
                switch (typeClassification) {
                    case conversationTypeClassifications.AutomatedEmail:
                        return { icon: ['fas', 'envelope'], color: 'orange' };
                    case conversationTypeClassifications.EmailOpened:
                        return { icon: ['fas', 'envelope-open'], color: 'green' };
                    case conversationTypeClassifications.FailedEmailDelivery:
                        return { icon: ['fas', 'envelope'], color: 'red' };
                    case conversationTypeClassifications.AutomatedSMS:
                        return { icon: ['fas', 'mobile-alt'], color: 'orange' };
                    case conversationTypeClassifications.SMSOpened:
                        return { icon: ['fas', 'mobile-alt'], color: 'green' };
                    case conversationTypeClassifications.FailedSMSDelivery:
                        return { icon: ['fas', 'mobile-alt'], color: 'red' };
                }
                break;
            case conversationTypes.EntityStateChange:
                if (typeClassification === conversationTypeClassifications.NewTicket ||
                    typeClassification === conversationTypeClassifications.ResolvedTicket) {
                    return { icon: ['far', 'question-circle'], color: 'orange' };
                }

                if (typeClassification === conversationTypeClassifications.PaymentPlan) {
                    const action = context && first(
                        context.split(';')
                            .filter((prop: any) => prop.startsWith('action='))
                            .map((prop: any) => prop.split('=')[1])
                    );

                    let color: string | undefined;

                    switch (action) {
                        case paymentPlanActions.Requested: color = 'orange'; break;
                        case paymentPlanActions.Approved: color = 'green'; break;
                        case paymentPlanActions.Completed: color = 'blue'; break;
                        case paymentPlanActions.Rejected:
                        case paymentPlanActions.Cancelled: color = 'red'; break;
                    }

                    return {
                        icon: ['fas', 'money-bill-alt'],
                        color
                    };
                }
                break;
            case conversationTypes.ResourceDiscussion:
                if (typeClassification === conversationTypeClassifications.ResourceRequest) {
                    return { icon: ['fas', 'share'], color: 'green' };
                }

                if (typeClassification === conversationTypeClassifications.ResourceReply) {
                    return { icon: ['fas', 'reply'], color: 'purple' };
                }
                break;
        }
    }
    // ------------------------------
    // INVOICE ITEM TYPE
    // ------------------------------
    else if (extraProps?.itemType === 'invoice' || extraProps?.itemType === 'salesOrder') {
        const source = get(item, 'Source');
        const itemTypeVal = get(item, 'Type');

        const getSourceIcon = (): string[] => {
            switch (source) {
                case 'User':
                case 'Customer':
                    return ['fas', 'user-edit'];
                case 'Import':
                    return ['fas', 'retweet'];
                default:
                    return [''];
            }
        };

        if (itemTypeVal === invoiceChangeTypes.NewResource) {
            return {
                icon: <FontAwesome icon={['fas', 'retweet']} className="green fs-16" />,
            };
        } else if (itemTypeVal === invoiceChangeTypes.UpdatedResource) {
            return {
                icon: <FontAwesome icon={getSourceIcon()} className="green fs-16" />,
            };
        }
    }
    // ------------------------------
    // TICKET ITEM TYPE
    // ------------------------------
    else if (extraProps?.itemType === 'ticket') {
        const state = get(item, 'State');

        const faIcon = (): string[] => {
            return state === 'Edited'
                ? ['fas', 'edit']
                : ['far', 'question-circle'];
        };

        const iconColor = () => {
            let color = 'orange';

            if (state === 'Closed' || state === 'Edited') {
                color = 'green';
            }

            return color;
        };

        return {
            icon: <FontAwesome icon={faIcon()} className={`${iconColor()} fs-16`} />,
        };
    }
    // ------------------------------
    // PAYMENT ITEM TYPE
    // ------------------------------
    else if (extraProps?.itemType === 'payment') {
        let iconBase: React.ReactNode;

        if (get(item, 'Type') === paymentChangeTypes.Allocation && get(item, 'Allocation.IsDiscount') === true) {
            iconBase = (
                <div className="fa-layers fa-fw fa fs-16">
                    <FontAwesome className="green" icon={['fa', 'file']} />
                    <FontAwesome
                        className="white"
                        icon={['fa', 'percent']}
                        transform="shrink-8 down-2"
                    />
                </div>
            );
        } else if (get(item, 'Type') === paymentChangeTypes.Allocation) {
            iconBase = (
                <div className="fa-layers fa-fw fa fs-16">
                    <FontAwesome className="green" icon={['fa', 'file']} />
                    <FontAwesome
                        className="white"
                        icon={['fa', 'plus']}
                        transform="shrink-8 down-2"
                    />
                </div>
            );
        } else if (get(item, 'Type') === paymentChangeTypes.NewResource) {
            iconBase = (
                <FontAwesome
                    icon={['fas', 'external-link-alt']}
                    className={`orange fs-16`}
                />
            );
        } else if (get(item, 'Type') === paymentChangeTypes.Settlement) {
            iconBase = (
                <FontAwesome
                    icon={['fas', 'credit-card']}
                    className={`green fs-16`}
                />
            );
        } else if (get(item, 'Type') === paymentChangeTypes.ResourceLink) {
            iconBase = (
                <div className="fa-layers fa-fw fa fs-16">
                    <FontAwesome
                        icon={['fas', 'receipt']}
                        className="green" />
                </div>
            );
        } else if (get(item, 'Type') === paymentChangeTypes.Deallocation) {
            if (get(item, 'Allocation.IsDiscount') === true) {
                iconBase = (
                    <div className="fa-layers fa-fw fa fs-16">
                        <FontAwesome className="red" icon={['fa', 'file']} />
                        <FontAwesome
                            className="white"
                            icon={['fa', 'percent']}
                            transform="shrink-8 down-2"
                        />
                    </div>
                );
            } else {
                iconBase = (
                    <div className="fa-layers fa-fw fa fs-16">
                        <FontAwesome className="red" icon={['fa', 'file']} />
                        <FontAwesome
                            className="white"
                            icon={['fa', 'plus']}
                            transform="shrink-8 down-2"
                        />
                    </div>
                );
            }
        } else if (get(item, "Type") === paymentChangeTypes.Disregarded) {
            iconBase = (
                <div className="fa-layers fa-fw fa fs-16">
                    <FontAwesome icon={["fas", "minus-circle"]} className="red" />
                </div>
            );
        } else if (get(item, "Type") === paymentChangeTypes.Verified) {
            iconBase = (
                <div className="fa-layers fa-fw fa fs-16">
                    <FontAwesome icon={["fas", "check-circle"]} className="green" />
                </div>
            );
        } else if (get(item, "Type") === paymentChangeTypes.Unverified) {
            iconBase = (
                <div className="fa-layers fa-fw fa fs-16">
                    <FontAwesome icon={["fas", "times-circle"]} className="red" />
                </div>
            );
        }

        const shouldShowDeallocate =
            extraProps?.allowedCreditsDeallocation &&
            extraProps?.allocatedAmount > 0 &&
            (get(item, 'Type') === paymentChangeTypes.ResourceLink || get(item, 'Type') === paymentChangeTypes.Allocation) &&
            get(item, 'IsReversed') !== true &&
            (get(item, 'Allocation') ? get(item, 'Allocation.IsDiscount') === false || get(item, 'Allocation.IsDiscount') === null : true);

        const deallocatePaymentHandler = () => {
            let deallocateResource: string = '';
            let isRemittanceAdviceDeallocation = false;

            if (get(item, 'Type') === paymentChangeTypes.ResourceLink) {
                deallocateResource = 'payment';
                isRemittanceAdviceDeallocation = true;
            } else if (get(item, 'Type') === paymentChangeTypes.Allocation) {
                if (startsWith(get(item, 'Resource.Uri'), 'cre')) {
                    deallocateResource = "credit from the invoice";
                } else if ((get(item, 'Context')).includes('overpayment')) {
                    deallocateResource = "payment from the credit";
                } else {
                    deallocateResource = "payment from the invoice";
                }
            }

            if (!modal) return;

            modal.confirm({
                title: getTranslatedText("Confirm deallocation"),
                content: (
                    <div>{getTranslatedText(`Are you sure you want to deallocate this ${deallocateResource}?`)}</div>
                ),
                onOk: () => {
                    if (extraProps?.deallocate) {
                        extraProps?.deallocate(get(item, 'Id'), isRemittanceAdviceDeallocation);
                    }
                },
                okText: getTranslatedText(confirmModalOkText),
                cancelText: getTranslatedText(confirmModalCancelText),
                getContainer: () => getPopoverContainer(extraProps?.containerRef),
            });
        };

        return {
                icon: iconBase,
                color: undefined,
                label: shouldShowDeallocate ? (
                    <Button
                        type="link"
                        onClick={deallocatePaymentHandler}
                        style={{ padding: '0 12px', whiteSpace: 'nowrap' }}
                    >
                        <FontAwesome icon={['fas', 'minus-square']} className="mr-4" />
                        <span>{getTranslatedText('Deallocate')}</span>
                    </Button>
                ) : undefined
            };

        // return {
        //     icon: (
        //         <span style={{ display: 'inline-flex', alignItems: 'center', transform: 'translateY(1px)', whiteSpace: 'nowrap' }}>
        //             {shouldShowDeallocate && (
        //                 <Popover content={getTranslatedText('Deallocate')}>
        //                     <Button
        //                         type="text"
        //                         danger
        //                         icon={<FontAwesome icon={['fas', 'minus-square']} />}
        //                         onClick={deallocatePaymentHandler}
        //                         style={{ padding: 0, height: 'auto', lineHeight: 1 }}
        //                     />
        //                 </Popover>
        //             )}
        //             <span style={{ lineHeight: 1 }}>{iconBase}</span>
        //         </span>
        //     ),
        //     color: undefined
        // };
    }
    // ------------------------------
    // REMITTANCE ADVICES ITEM TYPE
    // ------------------------------
    else if (extraProps?.itemType === 'remittance-advices') {
        let iconBase: React.ReactNode;

        if (get(item, "Type") === remittanceAdviceChangeTypes.NewResource) {
            let icon = "";

            switch (get(item, "Source")) {
                // Use import for old change lines, the rest will be accurate.
                case remittanceAdviceSource.Customer:
                    icon = "envelope";
                    break;
                case remittanceAdviceSource.User:
                case remittanceAdviceSource.System:
                    icon = "receipt";
                    break;
                default:
                    icon = "envelope";
                    break;
            }

            iconBase = (
                <div className="fa-layers fa-fw fa fs-16">
                    <FontAwesome icon={["fas", icon]} className="green" />
                </div>
            );
        } else if (get(item, "Type") === remittanceAdviceChangeTypes.Verified) {
            iconBase = (
                <div className="fa-layers fa-fw fa fs-16">
                    <FontAwesome icon={["fas", "search-dollar"]} className="green" />
                </div>
            );
        } else if (get(item, "Type") === remittanceAdviceChangeTypes.Processed) {
            iconBase = (
                <div className="fa-layers fa-fw fa fs-16">
                    <FontAwesome icon={["fas", "search-dollar"]} className="orange" />
                </div>
            );
        } else if (get(item, "Type") === remittanceAdviceChangeTypes.ResourceLink) {
            iconBase = (
                <div className="fa-layers fa-fw fa fs-16">
                    <FontAwesome icon={["fas", "receipt"]} className="green" />
                </div>
            );
        } else if (get(item, "Type") === remittanceAdviceChangeTypes.Deallocation) {
            iconBase = (
                <div className="fa-layers fa-fw fa fs-16">
                    <FontAwesome className="red" icon={["fa", "file"]} />
                    <FontAwesome className="white" icon={["fa", "plus"]} transform="shrink-8 down-2" />
                </div>
            );
        } else if (get(item, 'Type') === remittanceAdviceChangeTypes.UpdatedResource) {
            iconBase = (
                <div className="fa-layers fa-fw fa fs-16">
                    <FontAwesome icon={['fas', 'retweet']} className={`green fs-16`} />
                </div>
            );
        } else if (get(item, "Type") === remittanceAdviceChangeTypes.Disregarded) {
            iconBase = (
                <div className="fa-layers fa-fw fa fs-16">
                    <FontAwesome icon={["fas", "minus-circle"]} className="red" />
                </div>
            );
        }

        return {
            icon: (
                <span style={{ display: 'inline-flex', alignItems: 'center', transform: 'translateY(1px)', whiteSpace: 'nowrap' }}>
                    <span style={{ lineHeight: 1 }}>{iconBase}</span>
                </span>
            ),
            color: undefined
        };
    }
    // ------------------------------
    // WORKFLOW ITEM TYPE
    // ------------------------------
    else if (extraProps?.itemType === 'workflow') {
        let iconBase: React.ReactNode;

        switch (type) {
            case workflowChangeTypes.NewResource:
                iconBase = <FontAwesome icon={['fas', 'retweet']} className={`green fs-16`} />;
                break;
            case workflowChangeTypes.UpdatedResource:
                iconBase = <FontAwesome icon={['fas', 'user-edit']} className="green fs-16" />;
                break;
        }
        return {
            icon: (
                <span style={{ display: 'inline-flex', alignItems: 'center', transform: 'translateY(1px)', whiteSpace: 'nowrap' }}>
                    <span style={{ lineHeight: 1 }}>{iconBase}</span>
                </span>
            ),
            color: undefined
        };
    } else {
        switch (type) {
            case conversationTypes.Comment:
                {
                    switch (typeClassification) {
                        case conversationTypeClassifications.Comment:
                            return {
                                icon: ['fas', 'comment'],
                                color: isRecentComment ? 'blue' : 'grey',
                            };
                        default:
                            return defaultResult;
                    }
                }
            case conversationTypes.DeliveryTracking:
                {
                    switch (typeClassification) {
                        case conversationTypeClassifications.AutomatedEmail:
                            {
                                return {
                                    icon: ['fas', 'envelope'],
                                    color: 'orange',
                                };
                            }
                        case conversationTypeClassifications.EmailOpened:
                            {
                                return {
                                    icon: ['fas', 'envelope-open'],
                                    color: 'green',
                                };
                            }
                        case conversationTypeClassifications.FailedEmailDelivery:
                            {
                                return {
                                    icon: ['fas', 'envelope'],
                                    color: 'red',
                                };
                            }
                        case conversationTypeClassifications.AutomatedSMS:
                            {
                                return {
                                    icon: ['fas', 'mobile-alt'],
                                    color: 'orange',
                                };
                            }
                        case conversationTypeClassifications.SMSOpened:
                            {
                                return {
                                    icon: ['fas', 'mobile-alt'],
                                    color: 'green',
                                };
                            }
                        case conversationTypeClassifications.FailedSMSDelivery:
                            {
                                return {
                                    icon: ['fas', 'mobile-alt'],
                                    color: 'red',
                                };
                            }
                        default:
                            return defaultResult;
                    }
                }
            case conversationTypes.EntityStateChange:
                {
                    switch (typeClassification) {
                        case conversationTypeClassifications.NewTicket:
                        case conversationTypeClassifications.ResolvedTicket:
                            return {
                                icon: ['far', 'question-circle'],
                                color: 'orange',
                            };
                        case conversationTypeClassifications.PaymentPlan: {
                            const action = context && first(
                                context.split(';')
                                    .filter((prop: any) => prop.startsWith('action='))
                                    .map((prop: any) => prop.split('=')[1])
                            );

                            let color: string = "";

                            switch (action) {
                                case paymentPlanActions.Requested: color = 'orange'; break;
                                case paymentPlanActions.Approved: color = 'green'; break;
                                case paymentPlanActions.Completed: color = 'blue'; break;
                                case paymentPlanActions.Rejected:
                                case paymentPlanActions.Cancelled: color = 'red'; break;
                            }

                            return {
                                icon: ['fas', 'money-bill-alt'],
                                color: color,
                            };
                        }                            
                        default:
                            return defaultResult;
                    }
                }
            case conversationTypes.ResourceDiscussion:
                {
                    switch (typeClassification) {
                        case conversationTypeClassifications.ResourceRequest:
                            return {
                                icon: ['fas', 'share'],
                                color: 'green',
                            };
                        case conversationTypeClassifications.ResourceReply:
                            return {
                                icon: ['fas', 'reply'],
                                color: 'purple',
                            };
                        default:
                            return defaultResult;
                    }
                }
            default:
                return defaultResult;
        }
    }
    
    return defaultResult;
};


/**
 * Function for processing the payload for invoices and customers filter payload
 * @param filters
 */
export const populateFilterCalculationsForPayloadCustomer = (
    filters: DynamicObject,
    isChildView: boolean = false
) => {
    const filterParams = { ...filters };
    const contactData = filterParams.Contact;
    const customerData = filterParams.Customer;

    if (isChildView) {
        filterParams.Customer = get(customerData, 'DisplayName'); 
    } else {
        if (customerData) {
            if (get(customerData, 'CustomerIds')) {
                // This is to be used when user selects a customer item in the dropdown
                // Expecetd result: Distinct customer selected
                filterParams.ViewAssociatedCustomers = get(customerData, 'ViewAssociatedCustomers');
                filterParams.CustomerId = get(customerData, 'CustomerIds');
    
                delete filterParams.Customer;
            } else {
                // This is for when user inputs in the search box and presses enter/OK without selecting any item from the dropdown
                filterParams.Customer = get(customerData, 'DisplayName'); 
            }
        }
    }
    
    if (contactData) {
        filterParams.Contact = sum(get(contactData, 'Contact'));
        filterParams.InclusiveContact = get(contactData, 'InclusiveContact');
    }

    const userInputData = filterParams.UserInputData;

    if (userInputData) {
        filterParams.User = get(userInputData, 'Name');
        filterParams.FromCustomer = !isEmpty(
            get(userInputData, 'FromCustomer')
        );

        delete filterParams.UserInputData;
    }

    const resultData = filterParams.Result;

    if (resultData) {
        filterParams.Result = sum(resultData);
    }

    const type = filterParams.Type;

    if (type) {
        filterParams.Type = sum(type);
    }

    if (!isUndefined(filterParams.IsBlocked)) {
        let isBlockedValue: any = filterParams.IsBlocked;

        if (filterParams.IsBlocked === 'true') {
            isBlockedValue = true;
        } else if (filterParams.IsBlocked === 'false') {
            isBlockedValue = false;
        }

        filterParams.IsBlocked = isBlockedValue;
    }

    if (!isUndefined(filterParams.IsVip)) {
        let isVipValue: any = filterParams.IsVip;

        if (filterParams.IsVip === 'true') {
            isVipValue = true;
        } else if (filterParams.IsVip === 'false') {
            isVipValue = false;
        }

        filterParams.IsVip = isVipValue;
    }

    return filterParams;
};

/**
 * Function for processing the payload for invoices and customers filter payload
 * @param filters
 */
export const populateFilterCalculationsForPayloadInvoices = (
    filters: DynamicObject,
) => {
    const filterParams = { ...filters };
    const contactData = filterParams.Contact;
    const customerData = filterParams.Customer; 

    if (customerData) {
        if (get(customerData, 'CustomerIds')) {
            filterParams.ViewAssociatedCustomers = get(customerData, 'ViewAssociatedCustomers');
            filterParams.CustomerId = get(customerData, 'CustomerIds');

            delete filterParams.Customer;
        } else {
            filterParams.Customer = get(customerData, 'DisplayName');
        }
    }
    
    if (contactData) {
        filterParams.Contact = sum(get(contactData, 'Contact'));
        filterParams.InclusiveContact = get(contactData, 'InclusiveContact');
    }

    const userInputData = filterParams.UserInputData;

    if (userInputData) {
        filterParams.User = get(userInputData, 'Name');
        filterParams.FromCustomer = !isEmpty(
            get(userInputData, 'FromCustomer')
        );

        delete filterParams.UserInputData;
    }

    const resultData = filterParams.Result;

    if (resultData) {
        filterParams.Result = sum(resultData);
    }

    const type = filterParams.Type;

    if (type) {
        filterParams.Type = sum(type);
    }

    if (!isUndefined(filterParams.IsBlocked)) {
        let isBlockedValue: any = filterParams.IsBlocked;

        if (filterParams.IsBlocked === 'true') {
            isBlockedValue = true;
        } else if (filterParams.IsBlocked === 'false') {
            isBlockedValue = false;
        }

        filterParams.IsBlocked = isBlockedValue;
    }

    if (!isUndefined(filterParams.IsVip)) {
        let isVipValue: any = filterParams.IsVip;

        if (filterParams.IsVip === 'true') {
            isVipValue = true;
        } else if (filterParams.IsVip === 'false') {
            isVipValue = false;
        }

        filterParams.IsVip = isVipValue;
    }
    
    return filterParams;
};

/**
 * Function for processing the payload for all task related page
 */
const populateFilterCalculationsForPayloadTask = (filters: DynamicObject) => {
    const filterParams = { ...filters };

    if (filterParams.Type) {
        filterParams.Type = sum(filterParams.Type);
    }

    if (filterParams.DeliveryStatus) {
        filterParams.DeliveryStatus = sum(filterParams.DeliveryStatus);
    }

    if (filterParams.DeliveryType) {
        filterParams.DeliveryType = sum(filterParams.DeliveryType);
    }

    if (filterParams.ContactType) {
        filterParams.Contact = sum(filterParams.ContactType);

        delete filterParams.ContactType;
    }

    if (filterParams.Customer) {
        if (get(filterParams.Customer, 'CustomerIds')) {
            filterParams.ViewAssociatedCustomers = get(filterParams.Customer, 'ViewAssociatedCustomers');
            filterParams.CustomerIds = [].concat(get(filterParams.Customer, 'CustomerIds') || []);
        } else {
            filterParams.DisplayName = get(filterParams.Customer, 'DisplayName'); 
            filterParams.CustomerCode = get(filterParams.Customer, 'DisplayName');
        }

        delete filterParams.Customer;
    }

    if (filterParams.StatusType) {
        filterParams.Status = sum(filterParams.StatusType);

        delete filterParams.StatusType;
    }

    if (filterParams.WorkflowNameStep) {
        const workflowFilters: DynamicObject[] = [];

        forEach (filterParams.WorkflowNameStep, (value: string) => {
            if (includes(value, treeParentChildSeparator)) {
                const childArr = value.split(treeParentChildSeparator);
                const parent = get(childArr, 0);
                const child = get(childArr, 1);
                const workflowFilterIdx = findIndex(workflowFilters, [
                    'WorkflowId',
                    parent,
                ]);

                if (workflowFilterIdx < 0) {
                    workflowFilters.push({
                        WorkflowId: parent,
                        States: [child],
                    });
                } else {
                    const workflowObj = clone(
                        get(workflowFilters, workflowFilterIdx)
                    );
                    const workflowStates = clone(workflowObj.States);

                    if (!includes(workflowStates, child)) {
                        workflowStates.push(child);
                    }

                    workflowFilters[workflowFilterIdx] = {
                        ...workflowObj,
                        States: workflowStates,
                    };
                }
            }
        });

        filterParams.WorkflowFilters = workflowFilters;

        delete filterParams.WorkflowNameStep;
    }

    if (filterParams.Action) {
        filterParams.Action = sum(filterParams.Action);
    }

    return filterParams;
};

/**
 * Function for processing the payload for all ticket related page
 */
const populateFilterCalculationsForPayloadTicket = (filters: DynamicObject) => {
    const filterParams = { ...filters };

    if (filterParams.Type) {
        filterParams.TicketOptionReasons = filterParams.Type;

        delete filterParams.Type;
    }

    if (filterParams.Customer) {
        if (get(filterParams.Customer, 'CustomerIds')){
            filterParams.ViewAssociatedCustomers = get(filterParams.Customer, 'ViewAssociatedCustomers');
            filterParams.CustomerIds = [].concat(get(filterParams.Customer, 'CustomerIds') || []);
        } else {
            filterParams.DisplayName = get(filterParams.Customer, 'DisplayName'); 
            filterParams.CustomerCode = get(filterParams.Customer, 'DisplayName');
        }

        delete filterParams.Customer;
    }

    if (filterParams.Action) {
        filterParams.Action = sum(filterParams.Action);
    }

    return filterParams;
};

/**
 * Function for processing the payload for all organisations related page
 */
const populateFilterCalculationsForPayloadOrganisation = (
    filters: DynamicObject
) => {
    const filterParams = { ...filters };

    const statusParam = filterParams.Status;

    if (statusParam && !isEmpty(statusParam)) {
        if (statusParam.length === 1) {
            filterParams.IsClosed = includes(
                statusParam,
                companyStatuses.CLOSED
            ) ? true : false;
        }
    }

    if (statusParam) {
        delete filterParams.Status;
    }

    return filterParams;
};

/**
 * Function for processing the payload for all payment plans related page
 */
const populateFilterCalculationsForPayloadPaymentPlans = (
    filters: DynamicObject
) => {
    const filterParams = { ...filters };


    if (filterParams.Type) {
        filterParams.Type = sum(filterParams.Type);
    }

    if (filterParams.Customer) {
        filterParams.Customer = get(filterParams.Customer, 'DisplayName'); 
        filterParams.ViewAssociatedCustomers = get(filterParams.Customer, 'ViewAssociatedCustomers');
        filterParams.CustomerIds = [].concat(get(filterParams.Customer, 'CustomerIds') || []);
    }

    if (filterParams.StatusType) {
        filterParams.Status = sum(filterParams.StatusType);

        delete filterParams.StatusType;
    }

    return filterParams;
};

export const populateFilterCalculationsForPayloadPayments = (
    filters: DynamicObject,
) => {
    const filterParams = { ...filters };

    if (filterParams.Customer) {
        filterParams.ViewAssociatedCustomers = get(filterParams.Customer, 'ViewAssociatedCustomers');
        filterParams.CustomerIds = [].concat(get(filterParams.Customer, 'CustomerIds') || []);
        filterParams.Customer = get(filterParams.Customer, 'DisplayName'); 
    }

    const resultData = filterParams.Result;

    if (resultData) {
        filterParams.Result = sum(resultData);
    }

    return filterParams;
};

const populateFilterCalculationsForPayloadRemittanceAdvices = (
    filters: DynamicObject
) => {
    const filterParams = { ...filters };

    if (filterParams.RemittanceAdviceState) {
        filterParams.RemittanceAdviceState = sum(filterParams.RemittanceAdviceState);
    }

    if (!isUndefined(filterParams.MultipleCustomer)) {
        let multipleCustomerValue: any = filterParams.MultipleCustomer;

        if (filterParams.MultipleCustomer === 'true') {
            multipleCustomerValue = true;
        } else if (filterParams.MultipleCustomer === 'false') {
            multipleCustomerValue = false;
        }

        filterParams.MultipleCustomer = multipleCustomerValue;
    }

    if (filterParams.Customer) {
        filterParams.Customer = get(filterParams.Customer, 'DisplayName'); 
        filterParams.ViewAssociatedCustomers = get(filterParams.Customer, 'ViewAssociatedCustomers');
        filterParams.CustomerId = [].concat(get(filterParams.Customer, 'CustomerIds') || []);
    }

    return filterParams;
};

const populateFilterCalculationsForPayloadReports = (
    filters: DynamicObject,
    customerLabel: string
) => {
    const filterParams = { ...filters };

    if (filterParams.ReportTypes) {
        const reportTypeFilters: ReportTypeFilter[] = [];

        forEach (filterParams.ReportTypes, (value: string) => {
            if (includes(value, treeParentChildSeparator)) {
                const childArr = value.split(treeParentChildSeparator);
                let description: string = get(childArr, 1);

                if (description.includes(customerLabel)) {
                    description = description.replace(customerLabel, 'Customer');
                }

                const type: number = parseInt(get(childArr, 0));
                let currentFilter: ReportTypeFilter | undefined = find(reportTypeFilters, f => f.Type === type);

                if (currentFilter) {
                    currentFilter.Descriptions.push(description);
                } else {
                    currentFilter = {
                        Descriptions: [description],
                        Type: type
                    };
                    reportTypeFilters.push(currentFilter);
                }
            } else {
                const type: number = parseInt(value);
                reportTypeFilters.push({
                    Descriptions: [],
                    Type: type
                });
            }
        });

        if (!isEmpty(reportTypeFilters)) {
            filterParams.ReportTypeFilters = reportTypeFilters;
        }

        delete filterParams.ReportTypes;
    }

    if (filterParams.Status) {
        filterParams.Status = sum(filterParams.Status);
    }

    const userValue = filterParams.ActionedByUserId;

    if (userValue) {
        if (checkIfEmailIsValid(userValue)) {
            filterParams.UserEmailAddress = userValue;
        } else {
            filterParams.UserId = userValue;
        }

        delete filterParams.ActionedByUserId;
    }

    return filterParams;
};

/**
 * Function for processing the payload for all change lines
 */
const populateFilterCalculationsForPayloadChangeLines = (
    filters: DynamicObject
) => {
    const filterParams = { ...filters };

    if(filterParams.Resource) {
        const isFromDropdown = get(filterParams.Resource, 'isFromDropdown');
        if(isFromDropdown){
            const resourceData = get(filterParams.Resource, 'Resource');
            delete filterParams.Resource;
            filterParams.Resource = {Uri: resourceData};
        }
        else {
            const resourceData = get(filterParams.Resource, 'ResourceNumber');
            delete filterParams.Resource;
             filterParams.Resource = {ResourceNumber: resourceData};
        }
    }

    if (filterParams.Type) {
        filterParams.Type = sum(filterParams.Type);
    }

    return filterParams;
};

/**
 * Function that checks if the size of device used is Lg or below. Based on Ant design variables for Grid.
 */
export const getIfIsLg = () => {
    return window.innerWidth < xlMin;
};

/**
 * Function that returns the badge component display.
 * Used for correcting the bug found when scrolling in iOS safari if the badge is inside a virtualized list.
 * @param count - number to be shown inside the badge.
 * @param maxCount - the max number to be displayed for the badge. Ex. 99, if the count is > 99, then the badge will show 99+
 */
export const getBadgeDisplay = (count: number, maxCount?: number) => {
    if (count > 0) {
        let usedCount: string | number = count;
        const usedMaxCount = maxCount || 99;

        if (count > usedMaxCount) {
            usedCount = `${usedMaxCount}+`;
        }

        return <div className="ant-badge-count">{usedCount}</div>;
    }
};

/**
 * Function that gets the users complete name.
 * @param user - user object from API
 */
export const getUserFullName = (user: User) => {
    return !isEmpty(user)
        ? `${get(user, 'GivenName')} ${get(user, 'FamilyName')}`
        : '- -';
};

/**
 * Function that populates loading skeletons.
 * @param iteration - number of loading skeletons to show
 * @param loading - loading indicator
 * @param titleProp?: any
 * @param paragraphProp?: any
 */
export const getLoadingSkeletons = (
    iteration: number,
    loading: boolean,
    titleProp?: any,
    paragraphProp?: any,
) => {
    const skeletons = times(iteration, (key: number) => (
        <Skeleton
            key={key}
            active
            loading={loading}
            title={titleProp}
            paragraph={paragraphProp}
        />
    ));

    return <>{skeletons}</>;
};

// insertAt is now re-exported from arrayUtils at the top of the file

/**
 * Function that remove whitespaces from string.
 * @param str - string to remove the whitespaces from
 */
export const removeSpaces = (str: string) => {
    return str.replace(/\s/g, '');
};

/**
 * Function that calls a callback (empty the predefined filter) when
 * there are applied filter changes and dropdown filter state is undefined.
 * @param filters - filters object to compare to previously applied filter
 * @param tableFilters - filters object previously applied
 * @param dropdownStateValue - value of the dropdown state
 * @param fromFilterBar - boolean indicator if from FilterBar `Apply filters` button
 * @param callbackEmptyFilter - function to call when conditions are met
 */
export const emptyPredefinedFilterOnAppliedFilters = (
    filters: DynamicObject | undefined,
    tableFilters: DynamicObject | undefined,
    dropdownStateValue: any,
    fromFilterBar: boolean,
    callbackEmptyFilter: () => void
) => {
    const compactFilters = omitBy(filters, isEmpty);
    const compactTableFilters = omitBy(tableFilters, isEmpty);

    if (
        fromFilterBar &&
        !isEqual(compactFilters, compactTableFilters) &&
        !isEmpty(dropdownStateValue)
    ) {
        callbackEmptyFilter();
    }
};

/**
 * Common function that sets popover container based on the given containerRef.
 * @param containerRef - ref where any of the popover element will appear
 */
export const populatePopoverContainer = (containerRef?: any) => {
    return containerRef ? () => getPopoverContainer(containerRef) : () => document.body;
};

/**
 * Parser needed for rendering HTML strings to react instead of using `dangerouslySetInnerHTML`.
 */
const htmlParser = new Parser();

/**
 * Function that parses an html string to html object that can be rendered directly.
 * @param htmlString
 */
export const parseToHTML = (htmlString: string) => {
    return htmlParser.parse(htmlString);
};

/**
 * Function that gets the date values (mostly min and max).
 * @param filterValue - value of filter
 * @param useLocalTime - boolean indicator if localtime is to be used or not
 */
export const getDateFilterValues = (
    filterValue: any,
    isCalendarView: boolean,
    useLocalTime?: boolean    
) => {
    const filterSelected = get(filterValue, 'value');
    const {
        THIS_MONTH,
        NEXT_MONTH,
        LAST_MONTH,
        LAST_7_DAYS,
        LAST_30_DAYS,
        NEXT_7_DAYS,
        NEXT_30_DAYS,
        CUSTOM_DATE_RANGE,
        CUSTOM_DAYS_RANGE,
        NOW,
        TODAY,
        WITHIN_THIS_WEEK,
        WITHIN_NEXT_7_DAYS,
        LAST_WEEK_UP_TO_NOW,
        CREATE_DATE_ATB_CURRENT,
        CREATE_DATE_ATB_30_DAYS,
        CREATE_DATE_ATB_60_DAYS,
        CREATE_DATE_ATB_90_PLUS_DAYS,
        CREATE_DATE_ATB_1_MONTH,
        CREATE_DATE_ATB_2_MONTHS,
        CREATE_DATE_ATB_3_PLUS_MONTHS,
        DUE_DATE_ATB_NOT_DUE,
        DUE_DATE_ATB_CURRENT,
        DUE_DATE_ATB_30_DAYS,
        DUE_DATE_ATB_60_DAYS,
        DUE_DATE_ATB_90_PLUS_DAYS,
        DUE_DATE_ATB_1_MONTH,
        DUE_DATE_ATB_2_MONTHS,
        DUE_DATE_ATB_3_PLUS_MONTHS,
        AVAILABLE_DATE_ATB_NOT_AVAILABLE,
        AVAILABLE_DATE_ATB_CURRENT,
        AVAILABLE_DATE_ATB_30_DAYS,
        AVAILABLE_DATE_ATB_60_DAYS,
        AVAILABLE_DATE_ATB_90_PLUS_DAYS,
        AVAILABLE_DATE_ATB_1_MONTH,
        AVAILABLE_DATE_ATB_2_MONTHS,
        AVAILABLE_DATE_ATB_3_PLUS_MONTHS
    } = dateSelectOptions;
    let minDate: null | string | Dayjs = '';
    let maxDate: null | string | Dayjs = '';

    if (filterSelected === THIS_MONTH) {
        minDate = dayjs().startOf('month').format(dateFormatYYYYMMDDDash);
        maxDate = dayjs().endOf('month').format(dateFormatYYYYMMDDDash);
    } else if (filterSelected === NEXT_MONTH) {
        const nextMonth = dayjs().add(1, 'month');
        minDate = dayjs(nextMonth)
            .startOf('month')
            .format(dateFormatYYYYMMDDDash);

        maxDate = dayjs(nextMonth)
            .endOf('month')
            .format(dateFormatYYYYMMDDDash);
    } else if (filterSelected === LAST_MONTH) {
        const previousMonth = dayjs().subtract(1, 'month');

        minDate = dayjs(previousMonth)
            .startOf('month')
            .format(dateFormatYYYYMMDDDash);

        maxDate = dayjs(previousMonth)
            .endOf('month')
            .format(dateFormatYYYYMMDDDash);
    } else if (filterSelected === LAST_7_DAYS) {
        const { startDate, endDate } = getDayRangeDates('Last', 7);

        if (startDate) {
            minDate = dayjs(startDate).format(dateFormatYYYYMMDDDash);
        }

        if (endDate) {
            maxDate = dayjs(endDate).format(dateFormatYYYYMMDDDash);
        }
    } else if (filterSelected === LAST_30_DAYS) {
        const { startDate, endDate } = getDayRangeDates('Last', 30);

        if (startDate) {
            minDate = dayjs(startDate).format(dateFormatYYYYMMDDDash);
        }

        if (endDate) {
            maxDate = dayjs(endDate).format(dateFormatYYYYMMDDDash);
        }
    } else if (filterSelected === NEXT_7_DAYS) {
        const { startDate, endDate } = getDayRangeDates('Next', 7);

        if (startDate) {
            minDate = dayjs(startDate).format(dateFormatYYYYMMDDDash);
        }

        if (endDate) {
            maxDate = dayjs(endDate).format(dateFormatYYYYMMDDDash);
        }
    } else if (filterSelected === NEXT_30_DAYS) {
        const { startDate, endDate } = getDayRangeDates('Next', 30);

        if (startDate) {
            minDate = dayjs(startDate).format(dateFormatYYYYMMDDDash);
        }

        if (endDate) {
            maxDate = dayjs(endDate).format(dateFormatYYYYMMDDDash);
        }
    } else if (filterSelected === CUSTOM_DATE_RANGE) {
        const startDate = dayjs(get(filterValue, 'From'));
        const endDate = dayjs(get(filterValue, 'To'));

        if (startDate) {
            minDate = dayjs(startDate).format(dateFormatYYYYMMDDDash);
        }

        if (endDate) {
            maxDate = dayjs(endDate).format(dateFormatYYYYMMDDDash);
        }
    } else if (filterSelected === CUSTOM_DAYS_RANGE) {
        let startDate, endDate;

        if (get(filterValue, 'From') && get(filterValue, 'To')) {
            // if (filterNameRaw === 'ActionDate') {
            const fromValue = get(filterValue, 'From');

            const rangeTypeFrom = has(fromValue, 'Last') ? 'Last' : 'Next';
            const fromStart = getDayRangeDatesFromTo(
                rangeTypeFrom,
                fromValue[rangeTypeFrom]
            );

            const toValue = get(filterValue, 'To');
            const rangeTypeTo = has(toValue, 'Last') ? 'Last' : 'Next';
            const toEnd = getDayRangeDatesFromTo(
                rangeTypeTo,
                toValue[rangeTypeTo]
            );

            startDate = fromStart;
            endDate = toEnd;
        } else {
            const lastValue = get(filterValue, 'Last');
            const nextValue = get(filterValue, 'Next');

            if (lastValue !== undefined) {
                const { startDate: startVal, endDate: endVal } =
                    getDayRangeDates('Last', lastValue);
                startDate = startVal;
                endDate = endVal;
            } else if (nextValue !== undefined) {
                const { startDate: startVal, endDate: endVal } =
                    getDayRangeDates('Next', nextValue);
                startDate = startVal;
                endDate = endVal;
            }
        }

        if (startDate) {
            minDate = dayjs(startDate).format(dateFormatYYYYMMDDDash);
        }

        if (endDate) {
            maxDate = dayjs(endDate).format(dateFormatYYYYMMDDDash);
        }
    } else if (filterSelected === NOW) {
        minDate = null;
        maxDate = dayjs().format(dateFormatYYYYMMDDDash);
    } else if (filterSelected === TODAY) {
        minDate = dayjs().format(dateFormatYYYYMMDDDash);
        maxDate = dayjs().format(dateFormatYYYYMMDDDash);
    } else if (filterSelected === WITHIN_THIS_WEEK) {
        minDate = dayjs().startOf('week').format(dateFormatYYYYMMDDDash);
        maxDate = dayjs().endOf('week').format(dateFormatYYYYMMDDDash);
    } else if (filterSelected === WITHIN_NEXT_7_DAYS) {
        const { endDate } = getDayRangeDates('Next', 7);

        minDate = dayjs().format(dateFormatYYYYMMDDDash);
        maxDate = dayjs(endDate).format(dateFormatYYYYMMDDDash);
    } else if (filterSelected === LAST_WEEK_UP_TO_NOW) {
        const lastWeek = dayjs().subtract(1, 'week');

        minDate = lastWeek.startOf('week').format(dateFormatYYYYMMDDDash);
        maxDate = dayjs().format(dateFormatYYYYMMDDDash);
    } else if (
        filterSelected === CREATE_DATE_ATB_CURRENT ||
        filterSelected === DUE_DATE_ATB_CURRENT ||
        filterSelected === AVAILABLE_DATE_ATB_CURRENT
    ) {
        if (isCalendarView) {
            minDate = dayjs().startOf('month').format(dateFormatYYYYMMDDDash);
            maxDate = dayjs().endOf('month').format(dateFormatYYYYMMDDDash);
        } else {
            minDate = dayjs().subtract(29, 'days').format(dateFormatYYYYMMDDDash);
            maxDate = dayjs().format(dateFormatYYYYMMDDDash);
        }
    } else if (
        filterSelected === CREATE_DATE_ATB_30_DAYS ||
        filterSelected === DUE_DATE_ATB_30_DAYS ||
        filterSelected === AVAILABLE_DATE_ATB_30_DAYS
    ) {
        minDate = dayjs().subtract(59, 'days').format(dateFormatYYYYMMDDDash);
        maxDate = dayjs().subtract(30, 'days').format(dateFormatYYYYMMDDDash);
    } else if (
        filterSelected === CREATE_DATE_ATB_1_MONTH ||
        filterSelected === DUE_DATE_ATB_1_MONTH ||
        filterSelected === AVAILABLE_DATE_ATB_1_MONTH
    ) {
        const startMonth = dayjs().startOf('month').subtract(1, 'months').startOf('month');

        minDate = startMonth.format(dateFormatYYYYMMDDDash);
        maxDate = startMonth.endOf('month').format(dateFormatYYYYMMDDDash);
    } else if (
        filterSelected === CREATE_DATE_ATB_60_DAYS ||
        filterSelected === DUE_DATE_ATB_60_DAYS ||
        filterSelected === AVAILABLE_DATE_ATB_60_DAYS
    ) {
        minDate = dayjs().subtract(89, 'days').format(dateFormatYYYYMMDDDash);
        maxDate = dayjs().subtract(60, 'days').format(dateFormatYYYYMMDDDash);
    } else if (
        filterSelected === CREATE_DATE_ATB_2_MONTHS ||
        filterSelected === DUE_DATE_ATB_2_MONTHS ||
        filterSelected === AVAILABLE_DATE_ATB_2_MONTHS
    ) {
        const startMonth = dayjs().startOf('month').subtract(2, 'months').startOf('month');

        minDate = startMonth.format(dateFormatYYYYMMDDDash);
        maxDate = startMonth.endOf('month').format(dateFormatYYYYMMDDDash);
    } else if (
        filterSelected === CREATE_DATE_ATB_90_PLUS_DAYS ||
        filterSelected === DUE_DATE_ATB_90_PLUS_DAYS ||
        filterSelected === AVAILABLE_DATE_ATB_90_PLUS_DAYS
    ) {
        minDate = null;
        maxDate = dayjs().subtract(90, 'days').format(dateFormatYYYYMMDDDash);
    } else if (
        filterSelected === CREATE_DATE_ATB_3_PLUS_MONTHS ||
        filterSelected === DUE_DATE_ATB_3_PLUS_MONTHS ||
        filterSelected === AVAILABLE_DATE_ATB_3_PLUS_MONTHS
    ) {
        minDate = null;
        maxDate = dayjs().endOf('month').subtract(3, 'months').endOf('month').format(dateFormatYYYYMMDDDash);
    } else if (
        filterSelected === DUE_DATE_ATB_NOT_DUE ||
        filterSelected === AVAILABLE_DATE_ATB_NOT_AVAILABLE
    ) {
        if (isCalendarView) {
            minDate = dayjs().startOf('month').add(1, 'months').format(dateFormatYYYYMMDDDash);
        } else {
            minDate = dayjs().add(1, 'days').format(dateFormatYYYYMMDDDash);
        }

        maxDate = null;
    }

    const minDateWithTimeLocal = minDate && useLocalTime ? minDate + 'T00:00:00' : null;
    const maxDateWithTimeLocal = maxDate && useLocalTime ? maxDate + 'T23:59:59' : null;

    if (formatDateToDateObjectUTC) {
        if (minDateWithTimeLocal) {
            minDate = formatDateToDateObjectUTC(
                minDateWithTimeLocal,
                dateFormatYYYYMMDDTHHmmssDash,
                useLocalTime
            );
        }

        if (maxDateWithTimeLocal) {
            maxDate = formatDateToDateObjectUTC(
                maxDateWithTimeLocal,
                dateFormatYYYYMMDDTHHmmssDash,
                useLocalTime
            );
        }
    }

    return {
        minDate: !useLocalTime ? dayjs(minDate + 'T00:00:00.000Z', dateFormatYYYYMMDDTHHmmssSSSSSSUTCDash) : minDate,
        maxDate: !useLocalTime ? dayjs(maxDate + 'T23:59:59.000Z', dateFormatYYYYMMDDTHHmmssSSSSSSUTCDash) : maxDate,
    };
};

/**
 * Function that gets the dates based on range given and number of days
 * returns both start and end dates.
 * @param rangeType - Next, Last
 * @param days - number of days to add/subtract based on rangeType
 */
export const getDayRangeDates = (rangeType: 'Next' | 'Last', days: number) => {
    let startDate, endDate;

    if (rangeType === 'Next') {
        startDate = dayjs().add(1, 'day');
        endDate = dayjs(startDate).add(days - 1, 'days');
    } else if (rangeType === 'Last') {
        endDate = dayjs();
        startDate = dayjs(endDate).subtract(days, 'days');
    }

    return {
        startDate,
        endDate,
    };
};

/**
 * Function that gets the date based on rangeType and number of days
 * returns a single date (Dayjs object).
 * @param rangeType - Next, Last
 * @param days - number of days to add/subtract based on rangeType
 */
const getDayRangeDatesFromTo = (rangeType: 'Next' | 'Last', days: number) => {
    if (rangeType === 'Next') {
        return dayjs(dayjs().add(1, 'day')).add(days - 1, 'days');
    } else if (rangeType === 'Last') {
        return dayjs().subtract(days, 'days');
    }
};

/**
 * Function that formats date (assumed local) to UTC date object.
 * Returns Dayjs.
 * @param date - date in local date format
 * @param fromFormat - format of the date passed
 */
export const formatDateToDateObjectUTC = (
    date: any,
    fromFormat?: string | null,
    useLocalTime?: boolean
) => {
    const usedFromFormat = fromFormat || dateFormatYYYYMMDDTHHmmssDash;
    const localDatetime = dayjs(date, usedFromFormat).toDate();
    const storeState = store.getState();
    const companyTimezone = get(
        storeState,
        'companies.selectedUserCompany.Company.Timezone'
    );

    if (!useLocalTime && companyTimezone) {
        return dayjs
            .tz(
                dayjs(localDatetime).format(dateFormatYYYYMMDDTHHmmssDash),
                companyTimezone
            )
            .utc();
    } else {
        //if (!dayjs.utc(localDatetime).isValid()) {
        //    return dayjs.utc().format(usedFromFormat);
        //}

        return dayjs.utc(localDatetime);
    }
};

/**
 * Function that gets all the urls (with http:// or https://) outside an anchor tag
 * and converts it to a link (inside anchor tag).
 * @param htmlString
 */
export const urlifyURLsOutsideAnchorTags = (htmlString: string) => {
    const re = /((https?|http?):\/\/[^"<\s]+)(?![^<>]*>|[^"]*?<\/a)/gi;
    const urlsOutsideAnchor = htmlString.replace(re, (url: string) => {
        return '<a href="' + url + '" target="_blank">' + url + '</a>';
    });

    return urlsOutsideAnchor;
};

/**
 * Function that checks if the url string is a valid one.
 * @param url
 */
export const checkIfValidURL = (url: string) => {
    const pattern = new RegExp(
        // protocol identifier
        '(?:(?:(?:https?|ftp):)?//)' +
        // user:pass authentication
        '(?:\\S+(?::\\S*)?@)?' +
        '(?:' +
        // IP address exclusion
        // private & local networks
        '(?!(?:10|127)(?:\\.\\d{1,3}){3})' +
        '(?!(?:169\\.254|192\\.168)(?:\\.\\d{1,3}){2})' +
        '(?!172\\.(?:1[6-9]|2\\d|3[0-1])(?:\\.\\d{1,3}){2})' +
        // IP address dotted notation octets
        // excludes loopback network 0.0.0.0
        // excludes reserved space >= 224.0.0.0
        // excludes network & broacast addresses
        // (first & last IP address of each class)
        '(?:[1-9]\\d?|1\\d\\d|2[01]\\d|22[0-3])' +
        '(?:\\.(?:1?\\d{1,2}|2[0-4]\\d|25[0-5])){2}' +
        '(?:\\.(?:[1-9]\\d?|1\\d\\d|2[0-4]\\d|25[0-4]))' +
        '|' +
        // host name
        '(?:(?:[a-z\\u00a1-\\uffff0-9]-*)*[a-z\\u00a1-\\uffff0-9]+)' +
        // domain name
        '(?:\\.(?:[a-z\\u00a1-\\uffff0-9]-*)*[a-z\\u00a1-\\uffff0-9]+)*' +
        // TLD identifier
        '(?:\\.(?:[a-z\\u00a1-\\uffff]{2,})))' +
        // port number
        '(?::\\d{2,5})?' +
        // resource path
        '(?:[/?#]\\S*)?'
    ); // fragment locator

    return !!pattern.test(url);
};

/*
 * Function to set the last active cookie value to current timestamp.
 */
export const setCookieLastActiveCurrent = () => {
    const currentTimeStamp = dayjs().format(dateFormatTimestamp);
    Cookies.set(COOKIE_LAST_ACTIVE_NAME, currentTimeStamp, {
        path: '/',
    });
};

/*
 * Function to set the token if announcements should be fetched on login (prevents fetching on refresh).
 */
export const setShouldFetchAnnouncementsOnLogin = (value: 'true' | 'false') => {
    Cookies.set(FETCH_ANNOUNCEMENTS_ON_LOGIN_TOKEN_NAME, value, {
        path: '/',
    });
};

/**
 * Function that gets the last active time stored as cookie with the ms allowance.
 */
const getCookieLastActiveTimeWithAllowance = () => {
    const cookieLastActive = Cookies.get(COOKIE_LAST_ACTIVE_NAME);
    const allowanceTimeout = TIMEOUT_MILLISECOND - TIME_ACTIVE_COOKIE_SETTING;
    const cookieLastActiveWithAllowance = dayjs(
        cookieLastActive,
        dateFormatTimestamp
    ).add(allowanceTimeout, 'ms').format(dateFormatTimestamp);

    return cookieLastActiveWithAllowance;
};

/**
 * Function for getting a boolean if the user has been inactive within a period of time based on stored cookie.
 */
export const getBooleanIndicatorIfInactiveWithinPeriod = () => {
    const currentTimeStamp = dayjs().format(dateFormatTimestamp);
    const cookieLastActiveWithAllowance = getCookieLastActiveTimeWithAllowance();

    return dayjs(
        cookieLastActiveWithAllowance,
        dateFormatTimestamp
    ).isSameOrBefore(dayjs(currentTimeStamp, dateFormatTimestamp));
};

/**
 * Function that gets the number of milliseconds between the stored cookie active time and current timestamp.
 */
export const getDiffInMsLastActiveAndCurrentTimestamp = () => {
    const currentTimeStamp = dayjs().format(dateFormatTimestamp);
    const cookieLastActiveWithAllowance = getCookieLastActiveTimeWithAllowance();

    return dayjs(cookieLastActiveWithAllowance, dateFormatTimestamp).diff(
        dayjs(currentTimeStamp, dateFormatTimestamp),
        'ms'
    );
};

/**
 * Download to excel response handler (showing of modal response).
 */
export const downloadToExcelModalResponseHandler = (
    IsSuccess: boolean,
    Messages: string[] | undefined,
    modal: ReturnType<typeof App.useApp>['modal'],
    callback?: () => void
) => {
    const modalZIndex = 2000;

    if (IsSuccess) {
        modal.success({
            title: getTranslatedText("Data Extraction Started"),
            content: getTranslatedText("Data extraction process has started. You will be notified once it is finished together with the download link!"),
            onOk: () => {
                if (callback) callback();
            },
            okText: getTranslatedText("OK"),
            zIndex: modalZIndex
        });
    } else {
        let messageContent: React.ReactNode = getTranslatedText("We couldn't complete the request to start the data extraction process. Please try again later.");

        if (Messages !== undefined && !isEmpty(Messages)) {
            messageContent = (
                <Row gutter={[0, 12]}>
                    <Col span={24}>
                        {getTranslatedText(
                            "We couldn't complete the request to start the data extraction process. Please review the issue(s) below:"
                        )}
                    </Col>

                    {Messages.map((error: string, index: number) => (
                        <Col key={index} span={24}>
                            {getTranslatedText(error)}
                        </Col>
                    ))}
                </Row>
            );
        }

        modal.error({
            title: getTranslatedText("We couldn't start the data extraction process"),
            content: messageContent,
            okText: getTranslatedText("OK"),
            zIndex: modalZIndex
        });
    }
};

/**
 * Function for populating the title section of change role modal.
 */
export const populateDownloadToExcelModalTitle = () => `Download as Excel`;

/**
 * Function for populating the body section of change role modal.
 */
export const populateDownloadToExcelModalDisplayMessage = () =>
    `Please wait while we start the data extraction process`;

/**
 * Common function that downloads a file from the given presigned url.
 * @param presignedUrl
 * @param callback
 */
export const downloadFileFromPresignedUrl = (
    presignedUrl: string,
    callback?: () => void
) => {
    let fileName = '';

    fetch (presignedUrl)
        .then((response) => {
            const stringIndicator = '?X-Amz-Expires';
            const responseUrlArr = filter(
                response.url.split('/'),
                (stringElement: string) =>
                    includes(stringElement, stringIndicator)
            );

            const lastElement = responseUrlArr ? last(responseUrlArr) : '';

            if (lastElement) {
                fileName = get(
                    lastElement.match(`^(.+?)${stringIndicator}`),
                    1,
                    ''
                ).replace('?', '');
            }

            return response.blob();
        })
        .then((blob) => {
            if (callback) {
                callback();
            }

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName.replace(
                '.xlsx',
                `${dayjs().format(dateFormatFileDownload)}.xlsx`
            );
            document.body.appendChild(a); // we need to append the element to the dom -> otherwise it will not work in firefox
            a.click();
            a.remove(); //afterwards we remove the element again
        });
};

/**
 * Function for getting the days overdue string/label based on the number of days provided.
 * @param date - date in utc
 * @param days - number of days
 */
export const populateDaysOverdue = (date: string, days: number | null) => {
    let overdueString = '';
    if (days === 0) {
        overdueString = getTranslatedText('Due today');
    } else if (days === 1) {
        overdueString = getTranslatedText('Due yesterday');
    } else if (days === -1) {
        overdueString = getTranslatedText('Due tomorrow');
    } else {
        // if (!isNull(days) && days > 0) days++;
        overdueString = getTranslatedText('days overdue').replace('[days]', days !== null ? days.toString() : '');
    }

    return overdueString;
};

/**
 * Function that returns the sort field object based on the value selected
 * @param sortFieldValue - sort field value
 */
export const getSortFieldsWithCustomFields = (sortFieldValue: string) => {
    const sortObject: DynamicObject = {};

    if (includes(sortFieldValue, customFieldIndicator)) {
        const sortFieldValueArray = sortFieldValue
            .replace(customFieldIndicator, '')
            .split('--');
        sortObject.CustomFieldSort = JSON.stringify({
            Type: get(sortFieldValueArray, 0),
            Name: get(sortFieldValueArray, 1),
        });
    } else {
        sortObject.SortField = sortFieldValue;
    }

    return sortObject;
};

/**
* Common function for preparing the sort values inside the payload needed for connecting to API.
* @param sortBy
*/
export const populateSortFieldsParamSortParams = (sortBy: string) => {
   let sortFields: DynamicObject = {};

   if (includes(sortBy, customFieldIndicator)) {
       sortFields = getSortFieldsWithCustomFields(sortBy);
   } else {
       sortFields = { SortField: sortBy, ThenSortField: '' };

       if (sortBy === 'Type') {
           sortFields.SortField = 'Automation';
           sortFields.ThenSortField = 'Type';
       }
   }

   return sortFields;
};

/**
 * Function that fetches the class names for each atb value.
 */
export const trialBalanceColorClassNames = (
    ATBData: any,
    isDueDateView: boolean
) => {
    /**
     * Function for getting the greatest amount label for aged trial balance.
     * @param mapValue
     */
    const getGreatestAmountLabel = (mapValue: any) => {
        let greatestAmount = 0;
        let forReturnLabel: any = '';

        forEach (mapValue, (amount, amountLabel) => {
            if (amount > 0) {
                if (amount === greatestAmount) {
                    forReturnLabel = `${forReturnLabel}+++${amountLabel}`;
                } else if (amount > greatestAmount) {
                    greatestAmount = amount;
                    forReturnLabel = amountLabel;
                }
            }
        });

        return forReturnLabel;
    };

    const greatestAmountLabel: [] =
        getGreatestAmountLabel(ATBData).split('+++');

    const overdueLabels = ['Period1', 'Period2', 'Period3Plus'];

    if (isDueDateView) {
        overdueLabels.push('Current');
    }

    let greatestHasOverdue = false;

    forEach (overdueLabels, (overdueLabel: string) => {
        if (includes(greatestAmountLabel, overdueLabel)) {
            greatestHasOverdue = true;

            return false;
        }
    });

    /**
     * Function that gets the classname/color for the aged trial balance label.
     * @param trialBalanceLabel
     */
    const getTrialBalanceColorClassname = (
        trialBalanceLabel: typeof ATBValues[number]
    ) => {
        if (
            includes(greatestAmountLabel, trialBalanceLabel) &&
            greatestHasOverdue &&
            includes(overdueLabels, trialBalanceLabel)
        ) {
            return 'red ws-nw';
        } else if (
            includes(greatestAmountLabel, trialBalanceLabel) &&
            !greatestHasOverdue &&
            !includes(overdueLabels, trialBalanceLabel)
        ) {
            return 'green ws-nw';
        } else {
            return 'ws-nw';
        }
    };

    const ATBValues = [
        'Current',
        'Period1',
        'Period2',
        'Period3Plus',
        'NotDue',
    ];

    const ATBClassNames: DynamicObject = {};

    forEach (ATBValues, (atbName: string) => {
        ATBClassNames[atbName] = getTrialBalanceColorClassname(atbName);
    });

    return ATBClassNames;
};

/**
 * Function that handles opening of URLs in new tab & focuses it, and pop-up blocker detection
 * @param url
 * @param callback
 */
export const openURLNewTab = (
    url: string,
    modal: ReturnType<typeof App.useApp>['modal'],
    containerRef?: any,
    callback?: Function
) => {
    if (url.includes(MYOB_SHOW_ATTACHMENT_URL)) {
        // Handle MYOB API URL
        fetch(url, {
            method: 'GET',
            headers: {
                'Accept': 'application/pdf',
            },
        }).then(response => {
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            const contentType = response.headers.get('content-type');

            if (contentType && !contentType.includes('application/pdf') && !contentType.includes('application/octet-stream')) {
                throw new Error('Response is not a PDF file');
            }

            const text = response.text();

            return text;
        }).then(base64String => {
            const cleanBase64 = base64String.replace(/^data:application\/pdf;base64,/, '');
            
            // Convert Base64 string to binary
            const binaryString = atob(cleanBase64);
            const bytes = new Uint8Array(binaryString.length);
                
            for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
            }
                
            // Create a blob from the binary data
            const pdfBlob = new Blob([bytes], { type: 'application/pdf' });
                
            // Verify we have actual content
            if (pdfBlob.size === 0) {
                throw new Error('Received empty PDF data');
            }

            // Create a blob URL with PDF content type
            const blobUrl = window.URL.createObjectURL(pdfBlob);
            const cleanup = () => {
                window.URL.revokeObjectURL(blobUrl);
            };
                
            const newWin = window.open(blobUrl, '_blank');
                
            if (!newWin || newWin.closed || typeof newWin.closed == 'undefined') {
                modal.warning({
                    title: getTranslatedText("Pop-up blocker enabled"),
                    content: getTranslatedText("Please turn off your pop-up blocker for this site and refresh this page to proceed."),
                    okText: getTranslatedText("OK"),
                    getContainer: () => getPopoverContainer(containerRef),
                });
                    
                // Clean up the blob URL
                cleanup();

                return;
            }
                
            newWin.focus();

            if (callback) callback();

            newWin.addEventListener('beforeunload', cleanup);
            newWin.addEventListener('blur', cleanup);

            window.addEventListener('beforeunload', cleanup);
        }).catch(error => {
            console.error('Error fetching the document:', error);

            modal.warning({
                title: getTranslatedText("Open new tab failed"),
                content: getTranslatedText("Failed to open the document. Please try again later."),
                okText: getTranslatedText("OK"),
                getContainer: () => getPopoverContainer(containerRef),
            });
        });
            
        return;
    }

    const newWin = window.open(url, '_blank');

    if (!newWin || newWin.closed || typeof newWin.closed === 'undefined') {
        modal.warning({
            title: getTranslatedText("Pop-Up Blocker Enabled"),
            content: getTranslatedText("Please turn off your pop-up blocker for this site and refresh this page to proceed."),
            okText: getTranslatedText("OK"),
            getContainer: () => getPopoverContainer(containerRef),
        });
    } else {
        newWin.focus();

        if (callback) {
            callback();
        }
    }
};

/**
 * Merge 2 array of objects by key.
 * @param a1 - Original array (contains all)
 * @param a2 - Array of objects that will override the data on the original array
 * @param key - Key of object to compare
 */
export const mergeByKey = (
    a1: DynamicObject[],
    a2: DynamicObject[],
    key: string
) =>
    a1.map((itm) => ({
        ...itm,
        ...a2.find((item) => item[key] === itm[key] && item),
    }));

export const debounceEventHandler = (
    eventFunction: (...args: any) => any,
    delayInMs: number
) => {
    const debounced = debounce(eventFunction, delayInMs);

    return function (e?: any) {
        if (e) {
            e.persist();
        }

        return debounced(e);
    };
};

export const customFieldsTableColumnValues = (
    customFieldType: string,
    customFieldList: CustomField[],
    customFieldsDataIndex: string[]
) => {
    const customFieldsTableObject: DynamicObject = {};

    forEach (customFieldList, ({ Name, Value }: CustomField) => {
        const customFieldDataIndexName = `${customFieldIndicator}${customFieldType}--${Name}`;

        const isNameInIndexCaseInsensitive = some(
            customFieldsDataIndex,
            (invoiceDataIdxName: string) => {
                return invoiceDataIdxName === customFieldDataIndexName;
            }
        );

        if (isNameInIndexCaseInsensitive) {
            customFieldsTableObject[customFieldDataIndexName] = Value;
        }
    });

    return customFieldsTableObject;
};

export const dashboardBypassAPIFetch = (
    lastWidgetDetails: DynamicObject,
    widgetDetails: DynamicObject
) => {
    const refetchQuery = get(widgetDetails, 'refetchQuery');

    if (refetchQuery === true) {
        return false;
    } else if (!isEmpty(lastWidgetDetails)) {
        const differenceWidgetDetailKeys = reduce(
            widgetDetails,
            (result, value, key: any) => {
                return isEqual(value, lastWidgetDetails[key])
                    ? result
                    : result.concat(key);
            },
            []
        );

        const filteredKeys = difference(
            differenceWidgetDetailKeys,
            notQueryRelatedKeys
        );

        if (
            isEmpty(filteredKeys) ||
            (!isUndefined(refetchQuery) && refetchQuery === false)
        ) {
            return true;
        } else {
            return false;
        }
    } else {
        return false;
    }
};

/**
 * Function that checks filetype by magic number
 */
export const checkFileTypeByMagicNumber = (
    file: any,
    allowedFileTypes: string[],
    callback: (response?: boolean) => void
) => {
    const fileName = get(file, 'name');

    // Bypass txt file as file signatures for them are very random.
    if (fileName && fileName.split('.').pop() === 'txt') {
        return callback(includes(allowedFileTypes, `.txt`));
    }

    const fileReader = new FileReader();
    fileReader.readAsArrayBuffer(file);
    fileReader.onloadend = function (e) {
        const result = get(e, 'target.result');

        if (result && typeof result !== 'string') {
            const arr = new Uint8Array(result).subarray(0, 4);
            let fileSignature = '';

            for (let i = 0; i < arr.length; i++) {
                fileSignature += arr[i].toString(16);
            }

            const upperFileSignature = upperCase(fileSignature).replace(
                /\s/g,
                ''
            );

            let isValid = false;

            forEach (fileTypesSignature, (magicNumArray, fileType) => {
                const magicNumList = magicNumArray.join(',');

                if (includes(magicNumList, upperFileSignature)) {
                    if (includes(allowedFileTypes, `.${fileType}`)) {
                        isValid = true;

                        return false;
                    }
                }
            });

            callback(isValid);
        } else {
            callback(false);
        }

        // Check the file signature against known types
        //https://en.wikipedia.org/wiki/List_of_file_signatures
    };
};

/**
 * Function that gets the region config based on the list of data provided.
 */
export const getRegionConfigFromList = (
    companyRegion: string,
    keyData: DynamicObject[],
    settingsData: DynamicObject[]
) => {
    const keyConfig = get(filter(keyData, ['Region', companyRegion]), 0, {});
    const settingConfig = get(
        filter(settingsData, ['Region', companyRegion]),
        0,
        {}
    );

    return { ...keyConfig, ...settingConfig };
};

/**
 * Function that checks if the request should refetch based on error response
 */
export const checkShouldRequestRefetch = (err: any) =>
    includes(toString(err), 'Network Error') ||
    includes(get(err, 'errors.0.message'), 'Network Error') ||
    includes(get(err, 'response.data.Messages.0', ''), 'timed out') ||
    includes(toString(err), 'Failed to fetch') ||
    get(err, 'response.status') === 504;

const navigationKeys = [
    'Backspace',
    'Delete',
    'Tab',
    'Escape',
    'Enter',
    'Home',
    'End',
    'ArrowLeft',
    'ArrowRight',
    'Clear',
    'Copy',
    'Paste'
];

export const setCaretPosition = (ctrl: any, pos: number) => {
    if (ctrl.setSelectionRange) {
        ctrl.focus();
        ctrl.setSelectionRange(pos, pos);
    } else if (ctrl.createTextRange) {
        const range = ctrl.createTextRange();
        range.collapse(true);
        range.moveEnd('character', pos);
        range.moveStart('character', pos);
        range.select();
    }
}

export const limitOnlyNumber = (
    allowDecimal = true, limitDecimal = true, allowNegative = false):
    (event: KeyboardEvent<HTMLInputElement> | KeyboardEvent<HTMLTextAreaElement>
) => void => {
    return (event) => {
        if (
            navigationKeys.indexOf(event.key) > -1 || // Allow: navigation keys: backspace, delete, arrows etc.
            (event.key === 'a' && event.ctrlKey) || // Allow: Ctrl+A
            (event.key === 'c' && event.ctrlKey) || // Allow: Ctrl+C
            (event.key === 'v' && event.ctrlKey) || // Allow: Ctrl+V
            (event.key === 'x' && event.ctrlKey) || // Allow: Ctrl+X
            (event.key === 'a' && event.metaKey) || // Allow: Cmd+A (Mac)
            (event.key === 'c' && event.metaKey) || // Allow: Cmd+C (Mac)
            (event.key === 'v' && event.metaKey) || // Allow: Cmd+V (Mac)
            (event.key === 'x' && event.metaKey) || // Allow: Cmd+X (Mac)
            (event.key === '.'
                && allowDecimal && (!limitDecimal || event.currentTarget.value && !event.currentTarget.value.includes('.'))) ||
            (event.key === '-' && allowNegative)
        ) {
            return;
        }

        // Ensure that it is a number and stop the keypress
        if (!Number.isInteger(parseInt(event.key))) {
            event.preventDefault();
        }
    }
};

/**
 * BYPASS CORS
 * sends a request to the specified url from a form. this will change the window location.
 * @param {string} path the path to send the post request to
 * @param {object} params the paramiters to add to the url
 * @param {string} [method=post] the method to use on the form
 */
export const postBypassCORS = (
    path: string,
    params: DynamicObject,
    method = 'post'
) => {
    // The rest of this code assumes you are not using a library.
    // It can be made less wordy if you use one.
    const form = document.createElement('form');
    form.method = method;
    form.action = path;

    for (const key in params) {
        if (params.hasOwnProperty(key)) {
            const hiddenField = document.createElement('input');
            hiddenField.type = 'hidden';
            hiddenField.name = key;
            hiddenField.value = params[key];

            form.appendChild(hiddenField);
        }
    }

    document.body.appendChild(form);
    form.submit();
};

/**
 * Function for getting the dropdown value based on details from Company.
 */
export const getSelectedAccountingSystemBasedOnCompanyDetails = (
    selectedUserCompany: CompanyUserRole | undefined
) => {
    const companyImportType = get(
        selectedUserCompany,
        'Company.ImportType',
        companyImportTypes.EXCEL
    );
    const companyCloudImportType = get(
        selectedUserCompany,
        'Company.CloudImportType'
    );
    const companyApiImportType = get(
        selectedUserCompany,
        'Company.ApiImportType'
    );
    let selectedAccountingSystemFromCompany =
        accountingSystemOptions.IODM_CONNECT_WEB_API;

    if (companyImportType === companyImportTypes.EXCEL) {
        selectedAccountingSystemFromCompany = accountingSystemOptions.EXCEL;
    } else if (companyImportType === companyImportTypes.CLOUD) {
        if (companyCloudImportType === companyCloudImportTypes.XERO) {
            selectedAccountingSystemFromCompany = accountingSystemOptions.XERO;
        } else if (companyCloudImportType === companyCloudImportTypes.MYOB) {
            selectedAccountingSystemFromCompany = accountingSystemOptions.MYOB;
        }
    } else if (companyImportType === companyImportTypes.API) {
        if (companyApiImportType === companyApiImportTypes.LEGACY) {
            selectedAccountingSystemFromCompany =
                accountingSystemOptions.LEGACY_API;
        } else if (
            companyApiImportType === companyApiImportTypes.IODM_CONNECT_WEB
        ) {
            selectedAccountingSystemFromCompany =
                accountingSystemOptions.IODM_CONNECT_WEB_API;
        }
    } 

    return selectedAccountingSystemFromCompany;
};

/**
 * Function for getting sort by options if using cloud import type.
 */
export const getUsedSortByOptionsIfCloud = (
    usedSortByOptions: { label: string; value: string }[],
    isUsingCloudImportType: boolean
) => {
    return isUsingCloudImportType ? filter(
        usedSortByOptions,
        (option) => !includes(hiddenCloudImportFields, option.value)
    ) : clone(usedSortByOptions);
};

/**
 * Function to check if this row item task is ready.
 * Returns boolean.
 */
export const checkIfTaskReady = (state: string, actionDate: string) => {
    return state === 'Available' && checkIfDateHasPassed(actionDate);
};

/**
 * Common function to round a decimal number to certain decimal places.
 */
export const roundNumberToDecimalDigits = (
    value: number,
    decimalDigits: number = 2
) => (Math.round(value * 100) / 100).toFixed(decimalDigits);

/**
* Formatting percentage number to text with percent sign.
* @param percentageNumber
*/
export const formatPercentage = (percentageNumber: number) =>
    `${roundNumberToDecimalDigits(percentageNumber)}%`;

/**
 * Common function for getting the domain prefix based on environment. Used to replace [Environment] placeholder.
 */
export const getDomainPrefixBasedOnEnvironment = () => {
    const environment = getCurrentBuildEnvironment();
    let domainPrefix = '';

    if (environment === Environments.DEV) {
        domainPrefix = 'dev.';
    } else if (environment === Environments.TEST) {
        domainPrefix = 'test.';
    } else if (environment === Environments.UAT) {
        domainPrefix = 'uat.';
    } else if (environment === Environments.NONPROD) {
        domainPrefix = 'sandbox.';
    } else if (environment === Environments.PROD) {
        domainPrefix = '';
    }

    return domainPrefix;
};

/**
 * Function for formatting numbers to 1K or M or something.
 */
export const nFormatter = (num: number, digits: number = 1): string => {
    if (num < 0) {
        // If the number is negative, return the formatted number with a minus sign.
        return '-' + nFormatter(Math.abs(num), digits);
    }

    const lookup = [
        { value: 1, symbol: '' },
        { value: 1e3, symbol: 'k' },
        { value: 1e6, symbol: 'M' },
        { value: 1e9, symbol: 'G' },
        { value: 1e12, symbol: 'T' },
        { value: 1e15, symbol: 'P' },
        { value: 1e18, symbol: 'E' },
    ];
    const rx = /\.0+$|(\.[0-9]*[1-9])0+$/;
    const item = lookup
        .slice()
        .reverse()
        .find(function (item) {
            return num >= item.value;
        });

    return item
        ? (num / item.value).toFixed(digits).replace(rx, '$1') + item.symbol
        : '0';
};

/**
 * Common function for fetching the compact view state of a page.
 */
export const getIsCompactViewForPage = (
    userPrefs: DynamicObject[],
    pageName: string
) => {
    const filteredUserPref = filter(userPrefs, ['PageName', pageName]);

    return get(filteredUserPref, '0.IsCompactView');
};

/**
 * Function that replaces all instances of capitalized and lowercased Customer string in a string.
 */
export const replaceInstancesOfCustomerString = (
    wholeStr: string,
    replacedStr: string,
    isOrgView?: boolean
) => {
    return isOrgView
        ? wholeStr
        : wholeStr
            .replace(/Customer/g, capitalize(replacedStr))
            .replace(/customer/g, toLower(replacedStr));
};

/**
 * Common function for replacing all instances of string in text
 */
export const replaceAllStrDynamic = (
    wholeStr: string,
    replacedStr: string,
    replacedWithStr: string
) => {
    return wholeStr.split(replacedStr).join(replacedWithStr);
};

export const isDocumentNode = (v: unknown): v is DocumentNode =>
    !!v && typeof v === 'object' && (v as any).kind === 'Document';

/**
 * Function for getting query string from DocumentNode
 */
export const getGraphqlQueryString = (doc: DynamicObject) => {
    return print(doc);
};

/**
 * Function for getting current notification page type
 */
export const getNotificationType = (location: any) => {
    const { search } = location;
    const urlQueries = QueryString.parse(search);
    const type = urlQueries['i'] as string;

    return {
        isNewInvoice: type === 'n',
        type
    };
};

/**
 * Function for checking if a comment is recent or not
 */
export const checkIsRecentComment = (createdDateTime: any, numberOfDaysBeforeRecentCommentTurnedOff: number) => {
    const diffDays = dayjs().diff(dayjs(createdDateTime), 'days');

    return diffDays <= numberOfDaysBeforeRecentCommentTurnedOff;
};

/**
 * Function for checking if a scroll event already at bottom
 */
export const isScrollToBottom = (ev: any, padding: number = 10) => {
    const { scrollTop, scrollHeight, clientHeight } = ev.currentTarget;

    return (scrollTop + clientHeight) >= (scrollHeight - padding);
}

export const roundToDecimals = (value: number, decimals: number = 2) => {
    const pow = Math.pow(10, decimals);

    return Math.round(value * pow) / pow;
}

export const parseCsv = (csvStr: string, delimiter: string | undefined = undefined) => {
    const result = Papa.parse(csvStr, {
        delimiter,
        header: false
    });

    if (result.data) {
        return result.data as any[][];
    }

    throw new Error("Invalid csv data");
}

export const convertCamelToSentence = (input: string) => {
    return input
        .replace(/([a-z])([A-Z])/g, '$1 $2') // Split camelCase
        .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2') // Split PascalCase
        .toLowerCase() // Convert to lowercase
        .replace(/(^|\s)([a-z])/g, (_, space, letter) => space + letter.toUpperCase()); // Capitalize first letters
}

export const generateGUID = (): string => {
    return uuidv4();
};

export const computeOrganisationTabs = (userCompanies: Company[]) => {
    if (!userCompanies) return;

    userCompanies = orderBy(userCompanies, c => c.Region);
    const groupedCompanies = groupBy(userCompanies, c => `${c.Region} (${c.UsingCustomerWorkflow ? 'Customer Workflow' : 'Invoice Workflow'})`);
    const tabs: OrganisationActionTab[] = map(Object.entries(groupedCompanies), ([tabName, companies]) => ({
        tabName,
        region: companies[0].Region,
        usingCustomerWorkflow: companies[0].UsingCustomerWorkflow,
        companies: companies.map(c =>   {
            return {
                CompanyId:  c.CompanyId,
                Name: c.Name,
                IsCalendarView: c.IsCalendarView
            }
        })
    }));

    return tabs;
}

/**
 * Checks whether AWS credentials are available for Predictions/Translate.
 * Returns true if an Identity Pool is configured and credentials can be fetched.
 * Cached for 5 minutes to avoid repeated Cognito calls and rate limiting.
 */
let _translateCredsCache: { value: boolean; expiry: number } | null = null;
const TRANSLATE_CREDS_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

const hasTranslateCredentials = async (): Promise<boolean> => {
    const now = Date.now();
    if (_translateCredsCache && now < _translateCredsCache.expiry) {
        return _translateCredsCache.value;
    }

    try {
        const session = await fetchAuthSession();
        const result = !!(session.credentials && session.credentials.accessKeyId);
        _translateCredsCache = { value: result, expiry: now + TRANSLATE_CREDS_CACHE_TTL };
        return result;
    } catch {
        _translateCredsCache = { value: false, expiry: now + TRANSLATE_CREDS_CACHE_TTL };
        return false;
    }
};

// Translate text
export async function textTranslation(text: string) {
    try {
        if (!text || !text.trim()) {
            return text;
        }

        if (!(await hasTranslateCredentials())) {
            return text;
        }

        const result = await Predictions.convert({
            translateText: {
                source: {
                    text,
                    language: 'en',
                },
                targetLanguage: I18nWrapper.getCurrentLanguage(), // Specify the target language
            },
        });
        
        return result.text;
    } catch (error) {
        console.error('Error translating text', error);

        return text;
    }
}

const ensureCredentials = async () => {
    try {
        return await fetchAuthSession();
    } catch (error) {
        console.error('Error refreshing credentials:', error);
        throw error;
    }
};

export const translationText = async (text: string): Promise<string> => {
    if (!text || !text.trim()) {
        return text;
    }

    let translatedText: string = "";//I18n.get(text, "not existed");

    try {
        // Ensure valid credentials before making the request
        await ensureCredentials();

        if (!(await hasTranslateCredentials())) {
            return text;
        }

        //if (translatedText === "not existed") {
        const result = await Predictions.convert({
            translateText: {
                source: { text, language: 'en' },
                targetLanguage: I18nWrapper.getCurrentLanguage(), // Specify the target language
            },
        });
        
        translatedText = result.text;
        //}        
    } catch (error) {
        console.error('Error translating text', error);
        translatedText = text; // Fallback to original text in case of an error
    }

    return translatedText;
};

export const replaceTranslatedText = (template: string, replacements: Record<string, React.ReactNode>): React.ReactNode[] => {
    const parts = template.split(/(\[.*?\])/g);

    return parts.map((part, index) => {
        if (part.startsWith('[') && part.endsWith(']')) {
            const key = part.slice(1, -1);

            return replacements[key] || part;
        }

        return part;
    });
}

/**
 * In-memory cache for AWS Translate fallback results.
 * Key format: "languageCode:originalText" → translated text.
 */
const awsTranslateCache: Record<string, string> = {};
const awsTranslatePending: Record<string, boolean> = {};

export const getTranslatedText = (text: any) => {
    // 0. Guard against non-string or empty text
    if (text == null) return '';
    if (typeof text !== 'string') {
        // Return non-strings (React elements, objects, numbers) as-is — do not coerce
        return text;
    }
    if (!text.trim()) {
        return text;
    }

    // 1. Try local I18n dictionary first
    const localTranslation: string = I18n.get(text);
    if (localTranslation !== text) {
        return localTranslation;
    }

    // 2. Check current language - if English, no need to translate
    const currentLang = I18nWrapper.getCurrentLanguage();
    if (!currentLang || currentLang.startsWith('en')) {
        return text;
    }

    // 3. Check in-memory cache from previous AWS Translate calls
    const cacheKey = `${currentLang}:${text}`;
    if (awsTranslateCache[cacheKey]) {
        return awsTranslateCache[cacheKey];
    }

    // 4. Fire async AWS Translate call in background (only once per unique text)
    if (!awsTranslatePending[cacheKey]) {
        awsTranslatePending[cacheKey] = true;

        hasTranslateCredentials()
            .then((hasCreds) => {
                if (!hasCreds) {
                    delete awsTranslatePending[cacheKey];
                    return;
                }
                return Predictions.convert({
                    translateText: {
                        source: { text, language: 'en' },
                        targetLanguage: currentLang,
                    },
                }).then((result) => {
                    awsTranslateCache[cacheKey] = result.text;
                });
            })
            .catch((error) => {
                console.error('[AWS Translate] Failed:', text, error);
            })
            .finally(() => {
                delete awsTranslatePending[cacheKey];
            });
    }

    // First call: return original text (async result will be cached for next render)
    return text;
}

export const useTranslatedText = (text: string): string => {
    const [translatedText, setTranslatedText] = useState(I18n.get(text, "not existed"));

    useEffect(() => {
        if (translatedText === "not existed") {
            textTranslation(text).then(translated => {
                setTranslatedText(translated);
            });
        }
    }, [text, translatedText]);

    return translatedText;
};

export async function translatedText(text: string) {
    return await textTranslation(text);
};

export const useCompanyFlagValue = (flagName: keyof Company, usingCompany?: CompanyBase) => {
    const flagValue = useSelector((state: RootState) => getCompanyFlagValue(state, flagName, usingCompany));

    return flagValue;
};

export const getCompanyFlagValue = (state: RootState, flagName: keyof Company, usingCompany?: CompanyBase) => {
    if (usingCompany) {
        const userCompanies: CompanyUserRole[] = state.companies.userCompanies;
        const userCompany = userCompanies.find(uc => usingCompany && uc.Company.CompanyId === usingCompany.CompanyId);
        usingCompany = userCompany && userCompany.Company;

        if (usingCompany) {
            get(usingCompany, flagName);
        }
    }

    const currentUser = getCurrentUser(state);
    const isOrgView = get(currentUser, IsOrganisationViewAttribute) === '1';

    if (isOrgView) {
        const selectedUserOrganisation: Organisation | undefined = state.organisations.selectedUserOrganisation;
        const userCompanies: CompanyUserRole[] = state.companies.userCompanies;
        const orgUserCompanies = filter(userCompanies, uc => uc.Company.OrganisationId === get(selectedUserOrganisation, 'OrganisationId'));

        return some(orgUserCompanies, uc => (uc.Company as any)[flagName]);
    } else {
        const selectedUserCompany: CompanyUserRole | undefined = state.companies.selectedUserCompany;

        return get(selectedUserCompany, ['Company', flagName]);
    }
}

export const getNextMonth = (date: Dayjs) => {
    // Get the current year and month
    const nextMonth = date.clone().add(1, 'months');

    // Get the number of days in the next month
    const daysInNextMonth = nextMonth.daysInMonth();

    // Get the current day of the month
    let currentDay = date.date();

    // If the current day is greater than the number of days in the next month, use the last day of the next month
    if (currentDay > daysInNextMonth) {
        currentDay = daysInNextMonth;
    }

    // Set the day to the adjusted day and format the date
    return nextMonth.date(currentDay);
}

export const customPostData = async (url: string, data: any, headers: any = {}) => {
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...headers,
        },
        body: JSON.stringify(data),
    });

    if (!response.ok) {
        throw new Error('Failed to post data');
    }

    return response.json();
};

export const getUsingCompanyAndWorkflow = (
    isOrgView: boolean | undefined,
    selectedUserCompany: CompanyUserRole | undefined,
    selectedUserOrganisation: Organisation | undefined,
    userCompanies: CompanyUserRole[] | [],
    entityCompanyId: string | undefined
) => {
    const init: any = {
        selectedCompanyId: undefined,
        usingCustomerWorkflow: undefined,
        selectedCompanyInformation: undefined,
        selectedResourceId: undefined
    }

    const usingCompanyAndWorkflow: any = {
        ... init,
        selectedCompanyId: get(
            selectedUserCompany,
            'Company.CompanyId')
        ,
        usingCustomerWorkflow: get(
            selectedUserCompany,
            'Company.UsingCustomerWorkflow'
        ),
        selectedCompanyInformation: get(
            selectedUserCompany,
            'Company'
        ),
        selectedResourceId: get(
            selectedUserCompany,
            'Company.CompanyId'
        )
    };

    if (isOrgView) {
        const selectedOrganisationId = get(selectedUserOrganisation, 'OrganisationId');

        const validCompanies = filter(
            userCompanies,
            uc => uc.Company.OrganisationId === selectedOrganisationId
        );
        const company = validCompanies.find(com => com.Company.CompanyId === entityCompanyId);

        if (isUndefined(company)) {
            return init;
        }
        
        usingCompanyAndWorkflow.selectedCompanyId = company.Company.CompanyId;
        usingCompanyAndWorkflow.selectedCompanyInformation = company.Company;
        usingCompanyAndWorkflow.usingCustomerWorkflow = company.Company.UsingCustomerWorkflow;
        usingCompanyAndWorkflow.selectedResourceId = get(selectedUserOrganisation, 'OrganisationId');
    }

    return usingCompanyAndWorkflow;
};

export const getAccessToken = async () => {
    // Don't use forceRefresh: true - Amplify will automatically refresh tokens when needed
    const currentSession: DynamicObject = await fetchAuthSession();
    const accessToken = currentSession.tokens.accessToken;

    return accessToken.toString();
};

export const getRegionallyGraphqlResponse = async (
    regionBaseApi: string | undefined,
    query: any,
    variables?: any,
    customHeaders?: any
) => {
    if (!isUndefined(regionBaseApi) && !isEmpty(regionBaseApi)) {
        return await getRegionalApi(query, variables, regionBaseApi, customHeaders);
    }

    return await (client as any).graphql(
        {
            query,
            variables
        },
        {
            ...(customHeaders || {})
        }
    );
};

export const getRegionallyApiResponse = async (
    regionBaseApi: string | undefined,
    path: any,
    method: string,
    variables?: any
) => {
    if (!isUndefined(regionBaseApi) && !isEmpty(regionBaseApi)) {
        switch (method) {
            case 'POST': {
                return await postRegionalApi(path, variables, regionBaseApi);
            }
            case 'DELETE': {
                return await delRegionalApi(path, variables, regionBaseApi);
            }
            default:
                throw new Error(`Invalid method`);
        }
    }

    switch (method) {
        case 'POST': {
            return await executePostApi(path, variables);
        }
        case 'DELETE': {
            return await delApi(path, variables);
        }
        default:
            throw new Error(`Invalid method`);
    }
};

export const getOrganisationCurrencies = (
    selectedUserOrganisation: Organisation
): any[] => {
    let result: CreditOrganisationCurrency[] = [];
    const currencies = get(selectedUserOrganisation, 'Currencies');

    if (!isUndefined(currencies) && !isEmpty(currencies)) {
        result = currencies
            .map(item => ({
                Value: item.Value,
                Rate: item.Rate
            }));
    }

    return result;
};

export const getSelectedCurrencyRate = (
    selectedUserOrganisation: Organisation,
    currencyCode: string
): number | undefined => {
    let result: number | undefined;
    const currency = find(get(selectedUserOrganisation, 'Currencies'), [
        'Value',
        currencyCode,
    ]);

    if (!isUndefined(currency) &&  !isEmpty(currency)) {
        result = get(currency, 'Rate');
    }

    return result;
};

export const getDashboardFilterCompanyIds = (
    getFieldValue: (fieldName: string) => any,
    widgetDetails: any,
    commonOrgFormFields: any,
    organisationCompanies: any[],
    allOptionsSelectedKey: string,
    isUsingDefaultOptions: boolean = false
): any[] => {
    let selectedCompanyIds = getFieldValue('Companies');

    if (!selectedCompanyIds || isEmpty(selectedCompanyIds)) {
        const defaultFilterCompanyIds = get(widgetDetails, commonOrgFormFields.COMPANIES, []);
        selectedCompanyIds = filter(defaultFilterCompanyIds, (p) => p !== allOptionsSelectedKey);
    }

    if (isUsingDefaultOptions && (!selectedCompanyIds || isEmpty(selectedCompanyIds))) {
        const selectedRegion = getFieldValue('Region') || 'Australia';

        selectedCompanyIds = organisationCompanies.map((oc: any) => {
            let companyId = oc.CompanyId;

            if (oc.Region === selectedRegion) {
                companyId = oc.CompanyId;
            }

            return companyId;
        });
    }

    return selectedCompanyIds;
};

// src/utils/errors.ts
export const mapError = (message: string): string => {
    let responseMessage: string = message;

    if (/Username\/client id combination not found./i.test(message)) {
        responseMessage = "User does not exist.";
    } else if (/Password did not conform with policy: Password not long enough/i.test(message) || /Value at 'password' failed to satisfy constraint: Member must have length greater than or equal to 6/i.test(message)) {
        responseMessage = "Password must be at least 6 characters.";
    } else if (/Invalid email address format./i.test(message)) {
        responseMessage = "Please enter a valid email address.";
    } else if (/Custom auth lambda trigger is not configured for the user pool./i.test(message)) {
        responseMessage = "Password is required.";
    } else if (/Username cannot be empty/i.test(message)) {
        responseMessage = "Email is required.";
    } else if (/failed to satisfy constraint: Member must satisfy regular expression pattern:/i.test(message)) {
        responseMessage = "Please enter valid credentials.";
    }

    return responseMessage;
};

export const getRedirectUrlSearchParams = (location: any) => {
    return `${location.pathname}${location.search}${location.hash}`;
};

/**
 * Creates GraphQL options with customer portal headers if valid
 * @param {Object} customerPortalInformation - Customer portal information from state
 * @returns {Object} GraphQL request options with headers if needed
 */
export const getNewHeaderKeyForCustomerPortal = (
    customerId: string | undefined,
): DynamicObject | {} => {
    return {
        ...(
            !isUndefined(customerId) && !isEmpty(customerId) 
            ? { [CUSTOMER_PORTAL_API_HEADER_KEY]: customerId } 
            : {}
        ),
    };
};

export const renderTranslatedTextWithJSX = (translatedText: string) => {
    const parts = translatedText
        .split(/(<b>.*?<\/b>|<b>[^<]+<\/b>|<em>.*?<\/em>)/)
        .filter(Boolean);

    return (
        <span>
            {parts.map((part, i) => {
                // Handle <b>...</b>
                if (part.match(/^<b>.*<\/b>$/)) {
                    return <b key={i}>{part.replace(/<\/?b>/g, "")}</b>;
                }

                // Handle <em>...</em>
                if (part.match(/^<em>.*<\/em>$/)) {
                    return <em key={i}>{part.replace(/<\/?em>/g, "")}</em>;
                }

                // Plain text
                return <React.Fragment key={i}>{part}</React.Fragment>;
            })}
        </span>
    );
};

export function formatTranslatedMessage(
    template: string,
    replacements: Record<string, React.ReactNode> = {}
): React.ReactElement | string {
    if (!template) return template;

    const regex = /{([^}]+)}/g;
    const parts: React.ReactNode[] = [];

    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = regex.exec(template)) !== null) {
        const [placeholder, key] = match;

        if (match.index > lastIndex) {
            parts.push(template.slice(lastIndex, match.index));
        }

        parts.push(replacements[key] ?? placeholder);

        lastIndex = regex.lastIndex;
    }

    if (lastIndex < template.length) {
        parts.push(template.slice(lastIndex));
    }

    // If nothing was interpolated, return string
    const hasReactNodes = parts.some(
        (p) => typeof p !== 'string'
    );

    return hasReactNodes
        ? (
            <>
                {parts.map((p, i) => (
                    <React.Fragment key={i}>{p}</React.Fragment>
                ))}
            </>
        )
        : template;
}

export const toStandardTitleCase = (input = '') => {
    const lowercaseWords = new Set([
        'a', 'an', 'the', 'and', 'but', 'or', 'for', 'nor',
        'on', 'at', 'to', 'from', 'by', 'of', 'in', 'as'
    ]);

    // Helper: Capitalize a word unless it's in lowercaseWords
    const capitalizeWord = (word: string, isFirst: boolean) => {
        if (word === word.toUpperCase() && word.length > 1) return word; // Acronym

        const lower = word.toLowerCase();

        if (isFirst || !lowercaseWords.has(lower)) {
            return lower.charAt(0).toUpperCase() + lower.slice(1);
        }

        return lower;
    };

    // Split by space first
    return input
        .split(' ')
        .map((segment, index) =>
            // Further split by `/`, `-`, `_`, and title-case each part
            segment
                .split(/([\/\-_])/g) // Keep delimiters as separate elements
                .map((part, i, arr) =>
                    /[\/\-_]/.test(part)
                        ? part // Keep delimiter as-is
                        : capitalizeWord(part, index === 0 && i === 0)
                )
                .join('')
        )
        .join(' ');
};

export const pascalCaseToFlat = (input: string) => (input.replace(/[A-Z]/g, " $&"));

export const populatePaymentPlanFromConfigration = () => { };

export const getValidDayInMonth = (date: Dayjs, specificDayValue: number) => {
    let populatingDayValue = specificDayValue;
    const daysInMonth = date.daysInMonth();

    if (specificDayValue > daysInMonth) {
        populatingDayValue = daysInMonth;
    }

    if (specificDayValue < 1) {
        populatingDayValue = 1;
    }

    return date.date(populatingDayValue);
};

/**
 * Used to get and match the BPayReference custom field value.
 * @param bPayoptions - Object containing BPay payment option details
 * @param customerCustomFields - Array of custom fields with Name and Value properties
 * @returns The resolved reference value string for BPAY payment
 */
export const getReferenceValBpay = (
    bPayoptions: any,
    customerCustomFields: Array<{ Name: string; Value: string }>
): string => {
    const reference = get(bPayoptions, "BPayReference", '');
    const strippedRef = typeof reference === "string"
        ? reference.replace(/[\[\]]/g, "")
        : "";

    const matchingField = customerCustomFields.find(field => field.Name === strippedRef);

    if (
        typeof reference === "string" &&
        reference.includes("[") &&
        reference.includes("]")
    ) {
        return matchingField ? get(matchingField, "Value", "") : "";
    }

    return reference;
};

/** Common function that force downloads a file from the given presigned url.
 * @param presignedUrl
 */
export const forceDownloadFileFromPresignedUrl = (
    presignedUrl: string
) => {
    const link = document.createElement('a');
    link.href = presignedUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

export const sentenceCase = (s: string) => upperFirst(toLower(s));

export const measureWidth = (() => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d")!;

    return (text: string, font = "14px sans-serif") => {
        ctx.font = font;

        return ctx.measureText(text).width;
    };
})();

export const wrapLabel = (text: string, maxWidth: number, font = "14px sans-serif") => {
    // quick fit check(text, font));
    if (measureWidth(text, font) <= maxWidth) return [text];

    // word-wrap; fallback to char-wrap for long words
    const words = text.split(" ");
    const lines: string[] = [];
    let line = "";

    for (const w of words) {
        const test = line ? `${line} ${w}` : w;

        if (measureWidth(test, font) <= maxWidth) {
            line = test;
        } else {
            if (!line) {
                // word itself is too long -> hard wrap by characters
                let chunk = "";
                for (const ch of w) {
                    const t = chunk + ch;
                    if (measureWidth(t, font) <= maxWidth) chunk = t;
                    else {
                        lines.push(chunk);
                        chunk = ch;
                    }
                }
                if (chunk) line = chunk;
            } else {
                lines.push(line);
                line = w;
            }
        }
    }

    if (line) lines.push(line);

    return lines;
};

export const computeYAxisLabelWidth = (
    data: Array<Record<string, any>>,
    labelKey: string,
    wrapWidth: number,
    font: string,
    wrapLabel: (raw: string, wrapWidth: number, font: string) => string[],
    padding: number = 8
) => {
    let maxLineWidth = 0;

    for (const row of data ?? []) {
        const raw = String(row?.[labelKey] ?? "");
        const lines = wrapLabel(raw, wrapWidth, font);

        for (const line of lines) {
            const w = measureWidth(line, font);

            if (w > maxLineWidth) maxLineWidth = w;
        }
    }

    // The wrapped line width should never exceed wrapWidth, but we still min() to be safe
    const fitted = Math.min(maxLineWidth, wrapWidth);

    return Math.ceil(fitted + padding);
}

/**
* Interpolates React nodes into a translated string.
* Supports placeholders like {termsLink} inside the template.
*/
export function interpolateTranslation(
    template: string,
    values: Record<string, React.ReactNode> = {}
): React.ReactNode {
    if (!template) return template;

    const parts: React.ReactNode[] = [];

    // Regex to match placeholders: {something}
    const regex = /\[([^]]+)]/g;

    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = regex.exec(template)) !== null) {
        const [placeholder, key] = match;

        // Add the text before the placeholder
        if (match.index > lastIndex) {
            parts.push(template.substring(lastIndex, match.index));
        }

        // Add the React node for the placeholder
        if (values[key]) {
            parts.push(
                <React.Fragment key={key}>
                    {values[key]}
                </React.Fragment>
            );
        } else {
            // If no replacement found, keep the raw placeholder
            parts.push(placeholder);
        }

        lastIndex = regex.lastIndex;
    }

    // Add any remaining text after last placeholder
    if (lastIndex < template.length) {
        parts.push(template.substring(lastIndex));
    }

    return <>{parts}</>;
}

export const multipleEmailValidator = async (_: any, value: string) => {
    if (!value) {
        return Promise.resolve();
    }

    const emails = value
        .split(';')
        .map((e) => e.trim())
        .filter((e) => e);

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    const invalid = emails.find((email) => !emailRegex.test(email));

    if (invalid) {
        return Promise.reject(
            new Error(`Invalid email address: ${invalid}`)
        );
    }

    return Promise.resolve();
};